import React, { useEffect, useState, useRef } from "react";
import { VendorTaglineControl } from "../../components/VendorTaglineField";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Card,
  Table,
  Button,
  Form,
  Spinner,
  Modal,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import {
  FaEdit,
  FaTrash,
  FaImage,
  FaClock,
  FaCodeBranch,
  FaUpload,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaShoppingBag,
  FaBuilding,
  FaStore,
  FaCog,
  FaTruck,
  FaBullhorn,
  FaLock,
  FaRobot,
  FaInfoCircle,
  FaEnvelope,
  FaUserSecret,
  FaListAlt,
  FaUserLock,
} from "react-icons/fa";
import { Switch } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "utilities/user.js";
import { useGoogleMaps } from "../../GoogleMapsContext";
import VendorAddressAutocomplete from "../../components/VendorAddressAutocomplete";
import BrandOutletFormModal from "../../components/BrandOutletFormModal";
import { useCountry } from "../../context/CountryContext.js";
import { usePermissions } from "../../utilities/usePermissions.js";
import OpenHoursManager from "../../components/OpenHours/OpenHoursManager";
import ConfirmDialog from "../../components/ConfirmDialog";
import SignInAsVendorButton from "../../components/SignInAsVendorButton";
import SetVendorLoginAction from "../../components/VendorCredentials/SetVendorLoginAction";
import SearchBox, { SearchHits } from "../../components/SearchBox";
import {
  apiCheckBrandOwnerEmail,
  apiCreateBrand,
  apiCreateBrandOutlet,
  apiDeleteBrand,
  apiFetchBrandById,
  apiFetchBrandDirectory,
  apiFetchBrandFeaturedImageHistory,
  apiFetchBrands,
  apiResendBrandOwnerSetupLink,
  apiToggleBrandStatus,
  apiCleanupBrandMedia,
  apiRemoveBrandMedia,
  apiCreateBrandMediaUploadSignature,
  apiUpdateBrand,
  apiUpdateFeaturedBrandsSetting,
  apiUpdateVendorOpenHours,
} from "../../api/index";
import "@reach/combobox/styles.css";
import usePersistedToggle from "../../hooks/usePersistedToggle";
import AppSelect from "../../components/ui/AppSelect";
import SettingHelp from "../Settings/settingsHelp";

// ── Design tokens (match EditBrandProductForm) ─────────────────────────────
const BRAND_DARK = "#17385a";
const BRAND_MID  = "#0a578b";
const BRAND_LIGHT = "#eff6ff";
const SUCCESS = "#16a34a";

const styles = `
  .bm-page-header {
    background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MID} 100%);
    border-radius: 12px;
    padding: 20px 24px;
    color: white;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
  .bm-page-header h4 { margin: 0; font-size: 1.25rem; font-weight: 700; letter-spacing: 0.3px; }
  .bm-page-header p  { margin: 0; opacity: 0.8; font-size: 0.85rem; }
  /* The search row above the brands table. */
  .bm-toolbar {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    padding: 12px 20px;
    border-bottom: 1px solid #e9ecef;
    background: #fbfcfd;
  }
  .bm-search-wrap { flex: 1 1 300px; max-width: 480px; min-width: 220px; }
  .bm-search-empty {
    display: flex; flex-direction: column; align-items: center; gap: 10px;
    padding: 36px 20px; color: #6b7280; font-weight: 600;
  }
  .bm-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 2px 16px rgba(23,56,90,0.09);
    overflow: hidden;
  }
  /* Horizontal scroll for the brands table.
     ────────────────────────────────────────────────────────────────────────
     react-bootstrap's "responsive" prop is INERT on this dashboard. The theme
     (assets/scss/lbd/_responsive.scss) carries a Bootstrap-3-era override —
     at min-width 992px it sets .table-responsive to overflow: visible — which
     deletes the scrollport on every desktop screen. So the table simply
     overflowed its card, and .bm-card's overflow: hidden then guillotined
     whatever stuck out: an admin saw the Delete button sliced in half at the
     card edge with no way to reach it.

     This class is ours, so no theme media query reaches it. Every other
     <Table responsive> in the dashboard still has the same latent bug.

     NOTE: this comment lives inside a JS template literal — no backticks. */
  .bm-table-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }
  /* A visible-but-quiet scrollbar. The default macOS overlay bar only appears
     while scrolling, which is no use as a hint that there IS more table. */
  .bm-table-scroll::-webkit-scrollbar { height: 10px; }
  .bm-table-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
  .bm-table-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
    border: 2px solid #f1f5f9;
  }
  .bm-table-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
  /* Firefox only. Setting scrollbar-width/scrollbar-color unconditionally
     would be a downgrade: modern Chrome lets the standard properties WIN and
     then ignores every ::-webkit-scrollbar rule above, which puts the overlay
     bar back and loses the always-visible hint. Canaried on the live page. */
  @supports not selector(::-webkit-scrollbar) {
    .bm-table-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
  }
  /* The natural width of the columns, so the row never compresses into an
     unreadable stack instead of scrolling. */
  .bm-table { min-width: 1120px; }
  .bm-table {
    table-layout: auto;
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
  }
  .bm-table thead th {
    background: ${BRAND_DARK};
    color: white !important;
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 14px;
    border: none;
    white-space: nowrap;
  }
  .bm-table tbody tr {
    background: white;
    transition: background-color 0.16s ease;
    --bs-table-bg: transparent;
    --bs-table-accent-bg: transparent;
    --bs-table-striped-bg: transparent;
    --bs-table-active-bg: transparent;
    --bs-table-hover-bg: transparent;
    --bs-table-bg-state: transparent;
    --bs-table-bg-type: transparent;
  }
  .bm-table tbody td {
    vertical-align: middle;
    font-size: 0.875rem;
    padding: 10px 14px;
    background-color: transparent !important;
  }
  .bm-table thead th * { color: white !important; }
  .bm-table tbody tr:hover,
  .bm-table tbody tr.table-active,
  .bm-table tbody tr.bm-row-active {
    background: ${BRAND_LIGHT} !important;
  }
  .bm-table th.col-actions, .bm-table td.col-actions {
    width: 280px;
    min-width: 280px;
    max-width: 280px;
    text-align: center;
    vertical-align: middle !important;
  }
  /* Bootstrap's .table > * > * > * sets vertical-align: top with higher
     specificity than a single class selector. Force middle on every cell
     in our table so action buttons sit centred next to multi-line cells. */
  .bm-table > tbody > tr > td,
  .bm-table > thead > tr > th {
    vertical-align: middle !important;
  }
  /* Fixed-width Address column. Inline maxWidth on a single td has no
     effect under table-layout:auto — the column still grows with the
     widest cell. A real width on both the th and td locks it. */
  .bm-table th.col-address, .bm-table td.col-address {
    width: 180px;
    min-width: 180px;
    max-width: 180px;
    white-space: normal;
    word-break: break-word;
    vertical-align: middle;
    font-size: 0.8rem;
    color: #6b7280;
  }
  .bm-action-cell {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    width: 100%;
    background: transparent;
  }
  .bm-badge-active   { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; font-size: 0.7rem; padding: 3px 8px; border-radius: 20px; }
  .bm-badge-inactive { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; font-size: 0.7rem; padding: 3px 8px; border-radius: 20px; }
  .bm-badge-ai       { background: #f0f4ff; color: #3730a3; border: 1px solid #c7d2fe; font-size: 0.7rem; padding: 3px 8px; border-radius: 20px; }
  /* Two ways a brand can be off the storefront, and they are not the same
     thing: amber = never went live yet, grey = was live and got pulled. */
  .bm-badge-state             { display: inline-block; font-size: 0.68rem; font-weight: 600; padding: 2px 8px; border-radius: 20px; margin-top: 3px; letter-spacing: 0.01em; }
  .bm-badge-state--pending    { background: #fff4e5; color: #a35b00; border: 1px solid #fcd9a4; }
  .bm-badge-state--off        { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
  .bm-create-note    { display: flex; align-items: flex-start; gap: 0; font-size: 0.76rem; color: #a35b00; background: #fff8ee; border: 1px solid #fcd9a4; border-radius: 8px; padding: 7px 11px; max-width: 460px; line-height: 1.35; }
  .bm-action-btn { border: none; border-radius: 8px; font-size: 0.8rem; padding: 5px 10px; transition: opacity 0.15s; }
  .bm-action-btn:hover { opacity: 0.85; }
  .bm-modal-header {
    background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MID} 100%);
    color: white;
    padding: 18px 24px;
    border: none;
  }
  .bm-modal-header .btn-close { filter: invert(1) brightness(2); opacity: 0.85; }
  .bm-modal-header .modal-title { font-size: 1rem; font-weight: 700; color: white; }
  /* The dashboard theme pushes EVERY dialog down with transform: translate(0,30%)
     (.modal.show .modal-dialog), which clips tall modals at the top and makes
     them read as off-centre. Neutralise it for the brand modals and centre with
     a normal top margin so the header is always fully visible. */
  .modal.show .modal-dialog.compact-modal {
    transform: none !important;
    margin: 1.75rem auto !important;
  }
  .bm-section {
    background: white;
    border: 1px solid #e5eaf0;
    border-radius: 12px;
    margin-bottom: 16px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(23,56,90,0.05);
    scroll-margin-top: 70px;
  }
  .bm-section-header {
    background: linear-gradient(180deg, #f8fbfe 0%, #eef4fa 100%);
    border-bottom: 1px solid #e3ecf5;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .bm-sec-icon {
    width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MID} 100%);
    box-shadow: 0 1px 3px rgba(10,87,139,0.35);
  }
  .bm-sec-icon svg { font-size: 0.85rem; color: #fff !important; }
  .bm-sec-titlewrap { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
  .bm-sec-title { font-size: 0.95rem; font-weight: 700; color: ${BRAND_DARK}; letter-spacing: 0.2px; line-height: 1.2; }
  .bm-sec-sub { font-size: 0.72rem; color: #8494a6; font-weight: 500; line-height: 1.3; }
  .bm-section-body { padding: 16px; }
  /* Sticky in-modal section navigator */
  .bm-formnav {
    position: sticky; top: 0; z-index: 20;
    margin: 0 -24px 18px; padding: 10px 24px;
    background: rgba(248,250,252,0.94);
    backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    border-bottom: 1px solid #e5eaf0;
    display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  }
  .bm-formnav-label { font-size: 0.66rem; color: #8494a6; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; margin-right: 2px; }
  .bm-formnav-chip {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 0.74rem; font-weight: 600; color: ${BRAND_DARK};
    background: #fff; border: 1px solid #dbe6f1; border-radius: 999px;
    padding: 5px 11px; cursor: pointer; transition: all 0.14s; white-space: nowrap;
  }
  .bm-formnav-chip:hover { background: ${BRAND_LIGHT}; border-color: ${BRAND_MID}; color: ${BRAND_MID}; }
  .bm-formnav-chip svg { font-size: 0.72rem; color: ${BRAND_MID}; }
  .bm-label { font-size: 0.8rem; font-weight: 600; color: #374151; margin-bottom: 3px; }
  .bm-control { font-size: 0.85rem; border-color: #d1d5db; border-radius: 6px; }
  .bm-control:focus { border-color: ${BRAND_MID}; box-shadow: 0 0 0 3px rgba(10,87,139,0.12); }
  /* Bootstrap was-validated styling for bm-control fields */
  .was-validated .bm-control:invalid { border-color: #dc2626 !important; box-shadow: 0 0 0 3px rgba(220,38,38,0.12) !important; }
  .was-validated .bm-control:invalid + .invalid-feedback,
  .was-validated .bm-control:invalid ~ .invalid-feedback { display: block; }
  .invalid-feedback { font-size: 0.72rem; color: #dc2626; margin-top: 3px; }
  .bm-btn-primary {
    background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MID} 100%);
    border: none; border-radius: 8px; font-size: 0.85rem; font-weight: 600;
    padding: 8px 20px; color: white; transition: opacity 0.15s;
  }
  .bm-btn-primary:hover { opacity: 0.9; background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MID} 100%); }
  .bm-btn-secondary { border-radius: 8px; font-size: 0.85rem; }
  .bm-header-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .bm-toggle-pill {
    display: flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.15); border-radius: 8px;
    padding: 5px 10px; font-size: 0.8rem; cursor: pointer;
  }
  .bm-img-preview {
    display: flex; align-items: center; gap: 10px;
    border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px;
    background: #f8fafc;
  }
  .bm-switch-field {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0;
    cursor: pointer;
    padding: 9px 12px;
    border: 1px solid #e5eaf0;
    border-radius: 9px;
    background: #fbfcfe;
    transition: border-color 0.14s, background 0.14s, box-shadow 0.14s;
  }
  .bm-switch-field:hover {
    border-color: #c9dcee;
    background: #f4f9fd;
    box-shadow: 0 1px 4px rgba(23,56,90,0.06);
  }
  .bm-switch-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .bm-switch-label {
    font-size: 0.92rem;
    font-weight: 500;
    color: #334155;
    line-height: 1.35;
  }
  .bm-switch-help {
    font-size: 0.68rem;
    color: #94a3b8;
    line-height: 1.35;
    font-style: italic;
    opacity: 0.92;
  }
  .bm-media-spec {
    font-size: 0.72rem;
    line-height: 1.45;
    color: #374151;
    background: #f8fafc;
    border: 1px solid #dbe6f1;
    border-radius: 7px;
    padding: 8px 10px;
    margin-bottom: 8px;
  }
  .bm-media-spec strong { color: ${BRAND_DARK}; }
  .bm-media-preview {
    border: 1px solid #dbe6f1;
    border-radius: 8px;
    padding: 10px;
    background: #f8fafc;
  }
  .bm-media-frame {
    width: 100%;
    overflow: hidden;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
  }
  .bm-media-frame--card { aspect-ratio: 16 / 9; }
  .bm-media-frame--shop { aspect-ratio: 15 / 4; }
  .bm-media-frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .bm-media-caption {
    margin-top: 6px;
    font-size: 0.68rem;
    color: #64748b;
    line-height: 1.35;
  }
  .bm-media-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 52px;
    margin-top: 2px;
    padding: 10px 12px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    color: #64748b;
    font-size: 0.72rem;
    line-height: 1.4;
  }
  .bm-media-empty svg { flex: 0 0 auto; color: #94a3b8; }
  .bm-carousel-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }
  .bm-carousel-order {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    background: #e0f2fe;
    color: #075985;
    font-size: 0.69rem;
    font-weight: 700;
  }
  .bm-carousel-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }
  .bm-carousel-artwork {
    overflow: hidden;
    border: 1px solid #dbe6f1;
    border-radius: 8px;
    background: white;
  }
  .bm-carousel-artwork img {
    display: block;
    width: 100%;
    aspect-ratio: 15 / 4;
    object-fit: cover;
    background: #f1f5f9;
  }
  .bm-carousel-artwork-copy {
    padding: 7px 8px;
    color: #475569;
    font-size: 0.67rem;
    line-height: 1.35;
  }
  .bm-carousel-artwork-copy strong {
    display: block;
    color: ${BRAND_DARK};
    font-size: 0.7rem;
  }
  .bm-carousel-artwork-copy span {
    display: block;
    margin-top: 2px;
    color: #7c3aed;
    font-size: 0.61rem;
    font-weight: 700;
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────
const defaultB2BForm = () => ({
  legalName: "",
  industryCategory: "",
  industryCategoryTag: "",
  tagline: "",
  primaryColor: "#324D54",
  secondaryColor: "#c9a227",
  heroAlt: "",
  catalogueVisibility: "PUBLIC",
  hidePricesGlobally: false,
  priceTierType: "SINGLE",
  allowMixedSkuCart: true,
  brandType: "DAILY",
  pickupComplexity: "standard",
  verifiedVendorBadge: true,
  isAIShoppable: false,
  flows: {
    standard: true,
    brandedPackaging: false,
    brandedPackagingConfig: {
      formatsAccepted: ["PDF", "AI", "PNG", "SVG"],
      maxFileMb: 25,
      reviewSlaWorkingDays: 3,
      rejectionReasons: ["Low resolution", "Wrong dimensions", "Missing bleed", "Incomplete design", "Other"],
    },
    customQuote: false,
    customQuoteConfig: { introText: "", fields: [] },
    hybrid: false,
  },
  // Per-brand attribute dictionary used by the product creation form,
  // storefront card chips, detail modal spec table, and cart-time
  // variant picker. See models/brandModel.js for the wire shape.
  productSpecTemplate: { enabled: false, introText: "", fields: [] },
  fulfilment: {
    deliveryCoverage: "KAMPALA",
    leadTimeStandardDays: 2,
    leadTimeCustomDays: 14,
    minimumOrderValueUgx: 0,
    paymentTermsOffered: [],
    returnsPolicyText: "",
  },
  promotion: {
    promoteOnLanding: false,
    promoBannerText: "",
    featuredProductIds: [],
  },
  dashboardPrefs: {
    notifyEmailNewOrder: true,
    notifySmsNewOrder: false,
    notifyEmailArtwork: true,
    notifySmsArtwork: false,
    defaultOrderView: "ALL",
    autoDeclineWhenOos: false,
    allowOneClickReorder: true,
  },
  internalCommercial: {
    onboardingDate: "",
    accountManagerUserId: "",
    commissionRateAgreed: "",
    listingFeePaid: false,
    listingFeePaidAt: "",
    specialTerms: "",
    onboardingNotes: "",
  },
});

const BRAND_MEDIA_RULES = Object.freeze({
  logo: { width: 512, height: 512, ratioLabel: "1:1" },
  banner: { width: 1280, height: 720, ratioLabel: "16:9" },
  shopBanner: { width: 1500, height: 400, ratioLabel: "15:4" },
});

const mergeB2bFromBrand = (brand) => {
  const base = defaultB2BForm();
  const ic = brand.internalCommercial || {};
  return {
    ...base,
    legalName: brand.legalName || "",
    industryCategory: brand.industryCategory || "",
    industryCategoryTag: brand.industryCategoryTag || "",
    tagline: brand.tagline || "",
    primaryColor: brand.primaryColor || base.primaryColor,
    secondaryColor: brand.secondaryColor || base.secondaryColor,
    heroAlt: brand.heroAlt || "",
    catalogueVisibility: brand.catalogueVisibility || "PUBLIC",
    hidePricesGlobally: !!brand.hidePricesGlobally,
    priceTierType: brand.priceTierType || "SINGLE",
    allowMixedSkuCart: brand.allowMixedSkuCart !== false,
    brandType: brand.brandType || "DAILY",
    pickupComplexity: brand.pickupComplexity || "standard",
    verifiedVendorBadge: brand.verifiedVendorBadge !== false,
    isAIShoppable: !!brand.isAIShoppable,
    flows: {
      ...base.flows,
      ...(brand.flows || {}),
      brandedPackagingConfig: {
        ...base.flows.brandedPackagingConfig,
        ...(brand.flows?.brandedPackagingConfig || {}),
      },
      customQuoteConfig: {
        introText: brand.flows?.customQuoteConfig?.introText || "",
        fields: (brand.flows?.customQuoteConfig?.fields || []).map((f) => ({
          fieldKey: f.fieldKey || "",
          label: f.label || "",
          fieldType: f.fieldType || "text",
          required: !!f.required,
          placeholder: f.placeholder || "",
          options: (f.options || []).join(", "),
          helpText: f.helpText || "",
          order: f.order ?? 0,
        })),
      },
    },
    productSpecTemplate: {
      enabled: !!brand.productSpecTemplate?.enabled,
      introText: brand.productSpecTemplate?.introText || "",
      fields: (brand.productSpecTemplate?.fields || []).map((f) => ({
        fieldKey: f.fieldKey || "",
        label: f.label || "",
        fieldType: f.fieldType || "text",
        required: !!f.required,
        buyerConfigurableDefault: !!f.buyerConfigurableDefault,
        displayOnCard: !!f.displayOnCard,
        unit: f.unit || "",
        placeholder: f.placeholder || "",
        options: (f.options || []).join(", "),
        helpText: f.helpText || "",
        order: f.order ?? 0,
      })),
    },
    fulfilment: { ...base.fulfilment, ...(brand.fulfilment || {}) },
    promotion: { ...base.promotion, ...(brand.promotion || {}) },
    dashboardPrefs: { ...base.dashboardPrefs, ...(brand.dashboardPrefs || {}) },
    internalCommercial: {
      ...base.internalCommercial,
      onboardingDate: ic.onboardingDate ? String(ic.onboardingDate).slice(0, 10) : "",
      accountManagerUserId: ic.accountManagerUserId || "",
      commissionRateAgreed: ic.commissionRateAgreed !== undefined && ic.commissionRateAgreed !== null ? String(ic.commissionRateAgreed) : "",
      listingFeePaid: !!ic.listingFeePaid,
      listingFeePaidAt: ic.listingFeePaidAt ? String(ic.listingFeePaidAt).slice(0, 10) : "",
      specialTerms: ic.specialTerms || "",
      onboardingNotes: ic.onboardingNotes || "",
    },
  };
};

const brandToFormData = (brand = {}) => ({
  name: brand.name || "",
  tradingName: brand.tradingName || "",
  description: brand.description || "",
  address: brand.address || "",
  latitude: brand.latitude ?? null,
  longitude: brand.longitude ?? null,
  deliveryZoneRadius: brand.deliveryZoneRadius ?? 5,
  phone: brand.phone || "",
  email: brand.email || "",
  imageUrl: brand.imageUrl || "",
  bannerUrl: brand.bannerUrl || "",
  shopBannerUrl: brand.shopBannerUrl || "",
  fixedPrice: brand.fixedPrice ?? 0,
  timezone: brand.timezone || "Africa/Kampala",
  owner: brand.owner?._id || brand.owner || "",
  ...mergeB2bFromBrand(brand),
});

const useStyles = makeStyles((theme) => ({
  switchBase: {
    color: theme.palette.secondary.main,
    "&$checked": { color: theme.palette.secondary.main },
    "&$checked + $track": { backgroundColor: theme.palette.secondary.main },
  },
  checked: {},
  track: {},
}));

// ── Shared sub-components ──────────────────────────────────────────────────
const FormSection = ({ icon: Icon, title, subtitle, id, children }) => (
  <section id={id} className="bm-section">
    <div className="bm-section-header">
      {Icon && <span className="bm-sec-icon"><Icon /></span>}
      <span className="bm-sec-titlewrap">
        <span className="bm-sec-title">{title}</span>
        {subtitle ? <span className="bm-sec-sub">{subtitle}</span> : null}
      </span>
    </div>
    <div className="bm-section-body">{children}</div>
  </section>
);

// Sticky in-modal navigator: jump straight to any section of the (long) brand form.
const FORM_NAV_ITEMS = [
  { id: "sec-identity",   label: "Identity",   icon: FaStore },
  { id: "sec-location",   label: "Location",   icon: FaBuilding },
  { id: "sec-b2b",        label: "Catalogue",  icon: FaCog },
  { id: "sec-flows",      label: "Checkout",   icon: FaCog },
  { id: "sec-specs",      label: "Specs",      icon: FaListAlt },
  { id: "sec-fulfilment", label: "Fulfilment", icon: FaTruck },
  { id: "sec-promo",      label: "Promotion",  icon: FaBullhorn },
  { id: "sec-prefs",      label: "Dashboard",  icon: FaCog },
  { id: "sec-internal",   label: "Internal",   icon: FaLock, superOnly: true },
  { id: "sec-media",      label: "Media",      icon: FaImage },
];
const scrollToBrandSection = (id) => {
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};
const BrandFormNav = ({ isSuperAdmin }) => (
  <nav className="bm-formnav">
    <span className="bm-formnav-label">Jump to</span>
    {FORM_NAV_ITEMS.filter((i) => !i.superOnly || isSuperAdmin).map(({ id, label, icon: Icon }) => (
      <button type="button" key={id} className="bm-formnav-chip" onClick={() => scrollToBrandSection(id)}>
        <Icon />{label}
      </button>
    ))}
  </nav>
);

const ValidationBadge = ({ v }) => {
  if (!v) return null;
  const { isOptimal, isValid, message } = v;
  const color = isOptimal ? SUCCESS : isValid ? "#f59e0b" : "#dc2626";
  return (
    <span style={{ fontSize: "0.7rem", display: "flex", alignItems: "center", gap: 4 }}>
      {isOptimal
        ? <FaCheck style={{ color }} />
        : isValid
          ? <FaExclamationTriangle style={{ color }} />
          : <FaTimes style={{ color }} />}
      <span style={{ color }}>{message}</span>
    </span>
  );
};

// Shows all three validation checks (dimensions, format, size) stacked
const ImageValidationSummary = ({ validation }) => {
  if (!validation || (!validation.dimensions && !validation.format && !validation.size)) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
      <ValidationBadge v={validation.dimensions} />
      <ValidationBadge v={validation.format} />
      <ValidationBadge v={validation.size} />
    </div>
  );
};

const BrandSwitchField = ({ classes, checked, onChange, label, helperText, style }) => (
  <label className="bm-switch-field" style={style}>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      color="primary"
      classes={{ switchBase: classes.switchBase, track: classes.track, checked: classes.checked }}
    />
    <span className="bm-switch-copy">
      <span className="bm-switch-label">{label}</span>
      {helperText ? <span className="bm-switch-help">{helperText}</span> : null}
    </span>
  </label>
);

// ── Shared brand form body (used in both Create and Edit modals) ───────────
const BrandFormBody = ({ market, mapsApiLoaded, formData, setFormData, handleInputChange, handleSwitchChange, toggleFlowFlag, isSuperAdmin, handleLogoUpload, handleBannerUpload, handleShopBannerUpload, handleRemoveLogo, handleRemoveBanner, handleRemoveShopBanner, uploadingLogo, uploadingBanner, uploadingShopBanner, logoUploadProgress, bannerUploadProgress, shopBannerUploadProgress, logoValidation, bannerValidation, shopBannerValidation, removingMedia, classes, formSubmitAttempted, emailValidation, selectedBrandId, carouselArtwork, loadingBrandContent, brandContentError, onManageCarouselArtwork }) => (
  <>
    <BrandFormNav isSuperAdmin={isSuperAdmin} />
    {/* ── Core identity ── */}
    <FormSection id="sec-identity" icon={FaStore} title="Core Identity" subtitle="Name, description, contact & classification">
      <div className="row g-2">
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Brand Name <span className="text-danger">*</span></Form.Label>
            <Form.Control className="bm-control" size="sm" type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="e.g., Daily Monitor" />
            <div className="invalid-feedback">Brand name is required.</div>
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Trading Name</Form.Label>
            <Form.Control className="bm-control" size="sm" type="text" name="tradingName" value={formData.tradingName} onChange={handleInputChange} placeholder="e.g., Monitor Brands Ltd" />
          </Form.Group>
        </div>
        <div className="col-12">
          <Form.Group>
            <Form.Label className="bm-label">Description</Form.Label>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={(val) => setFormData((p) => ({ ...p, description: val }))}
              placeholder="Brief description of the brand…"
              style={{ background: "white", borderRadius: 6 }}
            />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Phone <span className="text-danger">*</span></Form.Label>
            <Form.Control className="bm-control" size="sm" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="+256700000000" />
            <div className="invalid-feedback">A valid phone number is required.</div>
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              className="bm-control"
              size="sm"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="contact@brand.com"
              isInvalid={emailValidation && !emailValidation.checking && (emailValidation.status === "staff_conflict" || emailValidation.status === "owned_by_other_brand" || emailValidation.status === "buyer_account_conflict")}
              isValid={emailValidation && !emailValidation.checking && (emailValidation.status === "available" || emailValidation.status === "current_owner" || emailValidation.status === "linkable_existing_user")}
            />
            <div className="invalid-feedback">
              {emailValidation?.message || "A valid email address is required."}
            </div>
            {emailValidation?.checking && (
              <small className="text-muted">Checking email availability…</small>
            )}
            {!emailValidation?.checking && emailValidation?.status === "linkable_existing_user" && (
              <small className="text-info">{emailValidation.message}</small>
            )}
            {!emailValidation?.checking && emailValidation?.status === "current_owner" && (
              <small className="text-success">Already linked to this brand owner.</small>
            )}
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Brand Type</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="brandType" value={formData.brandType} onChange={handleInputChange}>
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
            </AppSelect>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Pickup Complexity</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="pickupComplexity" value={formData.pickupComplexity} onChange={handleInputChange}>
              <option value="standard">Standard</option>
              <option value="moderate">Moderate</option>
              <option value="difficult">Difficult</option>
              <option value="premium">Premium</option>
            </AppSelect>
            <Form.Text className="bm-switch-help">Live now. The delivery fee engine multiplies the fare by this pickup level; the same field can be set on Settings, Delivery.</Form.Text>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Timezone</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="timezone" value={formData.timezone} onChange={handleInputChange}>
              <option value="Africa/Kampala">Africa/Kampala</option>
              <option value="Africa/Nairobi">Africa/Nairobi</option>
              <option value="UTC">UTC</option>
            </AppSelect>
          </Form.Group>
        </div>
      </div>
    </FormSection>

    {/* ── Location ── */}
    <FormSection id="sec-location" icon={FaBuilding} title="Location & Delivery Zone" subtitle="Pickup address, GPS coordinates & delivery radius">
      <div className="row g-2">
        <div className="col-12">
          <Form.Group>
            <Form.Label className="bm-label">Address <span className="text-danger">*</span></Form.Label>
            {/* key={market.country} remounts the autocomplete with the right
                market restriction when the operator switches country. */}
            <VendorAddressAutocomplete
              key={market.country}
              market={market}
              mapsApiLoaded={mapsApiLoaded}
              defaultValue={formData.address}
              onSelect={({ address, latitude, longitude }) =>
                setFormData((p) => ({ ...p, address, latitude, longitude }))
              }
              onClear={() =>
                setFormData((p) => ({ ...p, address: "", latitude: null, longitude: null }))
              }
            />
            <Form.Text style={{ fontSize: "0.75rem", color: "#6b7280" }}>Start typing to search. Select from suggestions to capture GPS coordinates.</Form.Text>
            {formData.latitude && formData.longitude && (
              <Form.Text className="text-success d-block mt-1" style={{ fontSize: "0.75rem" }}>
                ✓ Location set: {Number(formData.latitude).toFixed(6)}, {Number(formData.longitude).toFixed(6)}
              </Form.Text>
            )}
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Local Delivery Radius (km) <span className="text-danger">*</span></Form.Label>
            <Form.Control className="bm-control" size="sm" type="number" name="deliveryZoneRadius" value={formData.deliveryZoneRadius} onChange={handleInputChange} required min="1" step="0.1" />
            <div className="invalid-feedback">Delivery zone must be at least 1 km.</div>
            <Form.Text className="bm-switch-help">
              {formData.fulfilment?.deliveryCoverage === "NATIONAL"
                ? "Not used while coverage is National; all in-country destinations are eligible. Keep a sensible value for any future switch back to local coverage."
                : "Authoritative local coverage limit before any configured paid overage."}
            </Form.Text>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Legacy Fixed Price (UGX)</Form.Label>
            <Form.Control className="bm-control" size="sm" type="number" name="fixedPrice" value={formData.fixedPrice} onChange={handleInputChange} min="0" step="100" />
          </Form.Group>
        </div>
      </div>
    </FormSection>

    {/* ── B2B Identity & Catalogue ── */}
    <FormSection id="sec-b2b" icon={FaCog} title="B2B Identity & Catalogue" subtitle="Legal identity, branding colours & catalogue visibility">
      <div className="row g-2">
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Legal Name</Form.Label>
            <Form.Control className="bm-control" size="sm" name="legalName" value={formData.legalName} onChange={handleInputChange} />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Industry / Category</Form.Label>
            <Form.Control className="bm-control" size="sm" name="industryCategory" value={formData.industryCategory} onChange={handleInputChange} placeholder="e.g., Healthcare, Packaging" />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label className="bm-label">Industry Category Tag</Form.Label>
            <Form.Control className="bm-control" size="sm" name="industryCategoryTag" value={formData.industryCategoryTag} onChange={handleInputChange} placeholder="e.g., healthcare" />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <VendorTaglineControl
            size="sm"
            value={formData.tagline}
            onChange={handleInputChange}
            labelClassName="bm-label"
            controlClassName="bm-control"
          />
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Primary Colour (hex)</Form.Label>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="color" value={formData.primaryColor || "#324D54"} onChange={(e) => setFormData((p) => ({ ...p, primaryColor: e.target.value }))} style={{ width: 32, height: 32, border: "none", borderRadius: 4, cursor: "pointer", padding: 0 }} />
              <Form.Control className="bm-control" size="sm" name="primaryColor" value={formData.primaryColor} onChange={handleInputChange} style={{ flex: 1 }} />
            </div>
            <Form.Text className="bm-switch-help">
              Storefront structure, hero backdrop, headings and core brand identity.
            </Form.Text>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Secondary Colour (hex)</Form.Label>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="color" value={formData.secondaryColor || "#c9a227"} onChange={(e) => setFormData((p) => ({ ...p, secondaryColor: e.target.value }))} style={{ width: 32, height: 32, border: "none", borderRadius: 4, cursor: "pointer", padding: 0 }} />
              <Form.Control className="bm-control" size="sm" name="secondaryColor" value={formData.secondaryColor} onChange={handleInputChange} style={{ flex: 1 }} />
            </div>
            <Form.Text className="bm-switch-help">
              Explore Products, cart and checkout actions, accent lines and highlights.
            </Form.Text>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Hero Image Alt Text</Form.Label>
            <Form.Control className="bm-control" size="sm" name="heroAlt" value={formData.heroAlt} onChange={handleInputChange} />
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Catalogue Visibility</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="catalogueVisibility" value={formData.catalogueVisibility} onChange={handleInputChange}>
              <option value="PUBLIC">Standard (signed-in shoppers)</option>
              <option value="LOGIN_REQUIRED">Login required</option>
              <option value="VERIFIED_BUYERS">Verified buyers only</option>
            </AppSelect>
            <Form.Text className="bm-switch-help">Live now. The public brand API uses this to decide whether shoppers can browse without signing in.</Form.Text>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Price Tier Type</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="priceTierType" value={formData.priceTierType} onChange={handleInputChange}>
              <option value="SINGLE">Single price</option>
              <option value="TIERED">Tiered (retail / wholesale)</option>
              <option value="QUOTE">Quote-based</option>
            </AppSelect>
            <Form.Text className="bm-switch-help">Live now. This controls whether prices are public, tier-aware for signed-in buyers, or hidden behind quote requests.</Form.Text>
          </Form.Group>
        </div>
        <div className="col-md-4 d-flex align-items-end pb-1">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { id: "hidePricesGlobally",  name: "hidePricesGlobally",  checked: !!formData.hidePricesGlobally,  label: "Hide prices (enquiry mode)", helperText: "Live now. Product pages hide prices and push buyers toward quote or enquiry flows." },
              { id: "allowMixedSkuCart",   name: "allowMixedSkuCart",   checked: !!formData.allowMixedSkuCart,   label: "Allow mixed SKU cart", helperText: "Stored preference. Mixed-cart enforcement is not wired separately in this brand dashboard yet." },
              { id: "verifiedVendorBadge", name: "verifiedVendorBadge", checked: !!formData.verifiedVendorBadge, label: "Show official store badge", helperText: "Platform-owned trust mark. It deliberately keeps Lusuku colours instead of adopting the vendor palette." },
            ].map(({ id, name, checked, label, helperText }) => (
              <BrandSwitchField
                key={id}
                classes={classes}
                checked={checked}
                onChange={(nextChecked) => handleSwitchChange(name, nextChecked)}
                label={label}
                helperText={helperText}
              />
            ))}
          </div>
        </div>
      </div>
    </FormSection>

    {/* ── Checkout flows ── */}
    <FormSection id="sec-flows" icon={FaCog} title="Checkout Flows" subtitle="Which checkout journeys buyers can use">
      <div className="d-flex flex-wrap gap-4 mb-2">
        {[
          { key: "standard", label: "Standard checkout", helperText: "Live now. Buyers can place normal product orders without artwork or manual quoting." },
          { key: "brandedPackaging", label: "Branded / artwork", helperText: "Live now. Enables artwork-based packaging workflows and artwork upload notifications." },
          { key: "customQuote", label: "Custom quote", helperText: "Live now. Buyers can submit quote requests and dynamic custom fields for approval." },
          { key: "hybrid", label: "Hybrid (per-SKU override)", helperText: "Live now. Products can inherit the brand default or override their own order flow." },
        ].map(({ key, label, helperText }) => (
          <BrandSwitchField
            key={key}
            classes={classes}
            checked={!!formData.flows[key]}
            onChange={() => toggleFlowFlag(key)}
            label={label}
            helperText={helperText}
          />
        ))}
      </div>
      {formData.flows.customQuote && (
        <div style={{ background: BRAND_LIGHT, border: `1px solid #dbeafe`, borderRadius: 8, padding: 14, marginTop: 8 }}>
          <div className="bm-label" style={{ marginBottom: 8 }}>Custom Quote Form Config</div>
          <Form.Group className="mb-2">
            <Form.Label className="bm-label">Intro message shown to buyer</Form.Label>
            <Form.Control className="bm-control" size="sm" as="textarea" rows={2} placeholder="e.g. Please provide artwork details below…" value={formData.flows.customQuoteConfig?.introText || ""} onChange={(e) => setFormData((p) => ({ ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, introText: e.target.value } } }))} />
          </Form.Group>
          {(formData.flows.customQuoteConfig?.fields || []).map((field, idx) => (
            <div key={idx} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div className="row g-2">
                <div className="col-md-3">
                  <Form.Label className="bm-label">Label *</Form.Label>
                  <Form.Control className="bm-control" size="sm" placeholder="e.g. Paper stock" value={field.label}
                    onChange={(e) => setFormData((p) => { const f = [...p.flows.customQuoteConfig.fields]; f[idx] = { ...f[idx], label: e.target.value, fieldKey: e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") }; return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })} />
                </div>
                <div className="col-md-2">
                  <Form.Label className="bm-label">Type</Form.Label>
                  <AppSelect block className="bm-control" size="sm" value={field.fieldType}
                    onChange={(e) => setFormData((p) => { const f = [...p.flows.customQuoteConfig.fields]; f[idx] = { ...f[idx], fieldType: e.target.value }; return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })}>
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="select">Select</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="file">File</option>
                  </AppSelect>
                </div>
                <div className="col-md-3">
                  <Form.Label className="bm-label">Placeholder</Form.Label>
                  <Form.Control className="bm-control" size="sm" placeholder="e.g. 80gsm coated" value={field.placeholder || ""}
                    onChange={(e) => setFormData((p) => { const f = [...p.flows.customQuoteConfig.fields]; f[idx] = { ...f[idx], placeholder: e.target.value }; return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })} />
                </div>
                <div className="col-md-2 d-flex align-items-end pb-1">
                  <BrandSwitchField
                    classes={classes}
                    checked={!!field.required}
                    label="Required"
                    onChange={(nextChecked) => setFormData((p) => { const f = [...p.flows.customQuoteConfig.fields]; f[idx] = { ...f[idx], required: nextChecked }; return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })}
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <Button type="button" variant="outline-danger" size="sm" style={{ width: "100%" }}
                    onClick={() => setFormData((p) => { const f = p.flows.customQuoteConfig.fields.filter((_, i) => i !== idx); return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })}>
                    Remove
                  </Button>
                </div>
              </div>
              {field.fieldType === "select" && (
                <Form.Group className="mt-2">
                  <Form.Label className="bm-label">Options (comma-separated)</Form.Label>
                  <Form.Control className="bm-control" size="sm" placeholder="e.g. Glossy, Matte, Uncoated" value={field.options || ""}
                    onChange={(e) => setFormData((p) => { const f = [...p.flows.customQuoteConfig.fields]; f[idx] = { ...f[idx], options: e.target.value }; return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })} />
                </Form.Group>
              )}
              <Form.Group className="mt-2">
                <Form.Label className="bm-label">Help text</Form.Label>
                <Form.Control className="bm-control" size="sm" placeholder="Shown below the field label" value={field.helpText || ""}
                  onChange={(e) => setFormData((p) => { const f = [...p.flows.customQuoteConfig.fields]; f[idx] = { ...f[idx], helpText: e.target.value }; return { ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: f } } }; })} />
              </Form.Group>
            </div>
          ))}
          <Button type="button" variant="outline-primary" size="sm"
            onClick={() => setFormData((p) => ({ ...p, flows: { ...p.flows, customQuoteConfig: { ...p.flows.customQuoteConfig, fields: [...(p.flows.customQuoteConfig?.fields || []), { fieldKey: "", label: "", fieldType: "text", required: false, placeholder: "", options: "", helpText: "", order: (p.flows.customQuoteConfig?.fields || []).length }] } } }))}>
            + Add field
          </Button>
        </div>
      )}
    </FormSection>

    <FormSection id="sec-specs" icon={FaListAlt} title="Product sizes & specifications" subtitle="Set these on each product">
      <p className="mb-0">Open a product's create or edit form to add sizes, set the selling price of each size, and enter product specifications. These settings are saved with that product.</p>
    </FormSection>

    {/* ── Fulfilment ── */}
    <FormSection id="sec-fulfilment" icon={FaTruck} title="Fulfilment" subtitle="Coverage, lead times, minimums & payment terms">
      <div className="row g-2">
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Delivery Coverage</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="fulfilment.deliveryCoverage" value={formData.fulfilment.deliveryCoverage} onChange={handleInputChange}>
              <option value="KAMPALA">Kampala</option>
              <option value="KAMPALA_CENTRAL">Kampala + Central</option>
              <option value="NATIONAL">National</option>
              <option value="EX_WORKS">Ex-works only</option>
            </AppSelect>
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Lead Time Standard (days)</Form.Label>
            <Form.Control className="bm-control" size="sm" type="number" name="fulfilment.leadTimeStandardDays" value={formData.fulfilment.leadTimeStandardDays} onChange={handleInputChange} min="0" />
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Lead Time Custom (days)</Form.Label>
            <Form.Control className="bm-control" size="sm" type="number" name="fulfilment.leadTimeCustomDays" value={formData.fulfilment.leadTimeCustomDays} onChange={handleInputChange} min="0" />
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label className="bm-label">Minimum Order (UGX)</Form.Label>
            <Form.Control className="bm-control" size="sm" type="number" name="fulfilment.minimumOrderValueUgx" value={formData.fulfilment.minimumOrderValueUgx} onChange={handleInputChange} min="0" />
          </Form.Group>
        </div>
        <div className="col-md-8">
          <Form.Group>
            <Form.Label className="bm-label">Payment Terms (comma-separated)</Form.Label>
            <Form.Control className="bm-control" size="sm" placeholder="e.g. COD, 30-day, Prepay" value={(formData.fulfilment.paymentTermsOffered || []).join(", ")}
              onChange={(e) => setFormData((p) => ({ ...p, fulfilment: { ...p.fulfilment, paymentTermsOffered: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) } }))} />
          </Form.Group>
        </div>
        <div className="col-12">
          <Form.Group>
            <Form.Label className="bm-label">Returns Policy (short text)</Form.Label>
            <Form.Control className="bm-control" size="sm" name="fulfilment.returnsPolicyText" value={formData.fulfilment.returnsPolicyText} onChange={handleInputChange} />
          </Form.Group>
        </div>
      </div>
    </FormSection>

    {/* ── Landing promotion ── */}
    <FormSection id="sec-promo" icon={FaBullhorn} title="Landing Promotion" subtitle="Landing-page carousel & merchandising copy">
      <div className="row g-2 align-items-center">
        <div className="col-md-4 d-flex align-items-center" style={{ paddingTop: 6 }}>
          <BrandSwitchField
            classes={classes}
            checked={!!formData.promotion.promoteOnLanding}
            onChange={(nextChecked) => handleSwitchChange("promotion.promoteOnLanding", nextChecked)}
            label="Promote on landing page"
            helperText="When ON, this brand appears in the Featured Brands carousel on the public landing page. A brand can be active and featured without being promoted here — use this to control landing-page exposure at scale."
          />
        </div>
        <div className="col-md-8">
          <Form.Group>
            <Form.Label className="bm-label">Promo Banner Text (max 120 chars)</Form.Label>
            <Form.Control className="bm-control" size="sm" name="promotion.promoBannerText" value={formData.promotion.promoBannerText} onChange={handleInputChange} maxLength={120} />
            <Form.Text className="bm-switch-help">Stored merchandising copy for future landing-page and campaign surfaces.</Form.Text>
          </Form.Group>
        </div>
      </div>
    </FormSection>

    {/* ── Dashboard prefs ── */}
    <FormSection id="sec-prefs" icon={FaCog} title="Vendor Dashboard Preferences" subtitle="Vendor notifications & dashboard behaviour">
      <div className="row g-3">
        <div className="col-md-6">
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 10 }}>Notifications</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { name: "dashboardPrefs.notifyEmailNewOrder", checked: !!formData.dashboardPrefs.notifyEmailNewOrder, label: "Email on new order", helperText: "Legacy fallback. The vendor now manages email, SMS and WhatsApp alerts in Profile > Alert settings; this toggle only applies while they have not set a preference there." },
              { name: "dashboardPrefs.notifySmsNewOrder",   checked: !!formData.dashboardPrefs.notifySmsNewOrder,   label: "SMS on new order", helperText: "Legacy fallback. The vendor now manages email, SMS and WhatsApp alerts in Profile > Alert settings; this toggle only applies while they have not set a preference there." },
              { name: "dashboardPrefs.notifyEmailArtwork",  checked: !!formData.dashboardPrefs.notifyEmailArtwork,  label: "Email on artwork upload", helperText: "Live now. Sends an email when branded-packaging artwork is submitted." },
              { name: "dashboardPrefs.notifySmsArtwork",    checked: !!formData.dashboardPrefs.notifySmsArtwork,    label: "SMS on artwork upload", helperText: "Stored preference only. Artwork SMS notifications are not wired yet." },
            ].map(({ name, checked, label, helperText }) => (
              <BrandSwitchField
                key={name}
                classes={classes}
                checked={checked}
                onChange={(nextChecked) => handleSwitchChange(name, nextChecked)}
                label={label}
                helperText={helperText}
              />
            ))}
          </div>
        </div>
        <div className="col-md-6">
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 10 }}>Behaviour</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { name: "dashboardPrefs.autoDeclineWhenOos",   checked: !!formData.dashboardPrefs.autoDeclineWhenOos,   label: "Auto-decline when out of stock", helperText: "Stored preference only. Automatic stock-based decline is not wired into brand orders yet." },
              { name: "dashboardPrefs.allowOneClickReorder", checked: !!formData.dashboardPrefs.allowOneClickReorder, label: "Allow one-click reorder", helperText: "Stored preference only. A reorder shortcut has not been wired into the dashboard yet." },
            ].map(({ name, checked, label, helperText }) => (
              <BrandSwitchField
                key={name}
                classes={classes}
                checked={checked}
                onChange={(nextChecked) => handleSwitchChange(name, nextChecked)}
                label={label}
                helperText={helperText}
              />
            ))}
          </div>
          <Form.Group style={{ marginTop: 14 }}>
            <Form.Label className="bm-label">Default Order View</Form.Label>
            <AppSelect block className="bm-control" size="sm" name="dashboardPrefs.defaultOrderView" value={formData.dashboardPrefs.defaultOrderView} onChange={handleInputChange}>
              <option value="ALL">All orders</option>
              <option value="PENDING_ARTWORK">Pending artwork</option>
              <option value="AWAITING_FULFILMENT">Awaiting fulfilment</option>
              <option value="COMPLETED">Completed</option>
            </AppSelect>
            <Form.Text className="bm-switch-help">Stored preference only. The brand dashboard does not yet auto-open to this filter.</Form.Text>
          </Form.Group>
        </div>
      </div>
    </FormSection>

    {/* ── Internal (Lusuku only) ── */}
    {isSuperAdmin && (
      <FormSection id="sec-internal" icon={FaLock} title="Internal — Lusuku Only" subtitle="Commercials & finance — visible to Lusuku staff only">
        <div className="row g-2">
          <div className="col-md-4">
            <Form.Group>
              <Form.Label className="bm-label">Onboarding Date</Form.Label>
              <Form.Control className="bm-control" size="sm" type="date" name="internalCommercial.onboardingDate" value={formData.internalCommercial.onboardingDate} onChange={handleInputChange} />
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group>
              <Form.Label className="bm-label">Commission % Agreed</Form.Label>
              <Form.Control className="bm-control" size="sm" type="number" name="internalCommercial.commissionRateAgreed" value={formData.internalCommercial.commissionRateAgreed} onChange={handleInputChange} min="0" step="0.5" />
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group>
              <Form.Label className="bm-label">Account Manager User ID</Form.Label>
              <Form.Control className="bm-control" size="sm" name="internalCommercial.accountManagerUserId" value={formData.internalCommercial.accountManagerUserId} onChange={handleInputChange} placeholder="Mongo ObjectId" />
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group>
              <Form.Label className="bm-label">Listing Fee Paid At</Form.Label>
              <Form.Control className="bm-control" size="sm" type="date" name="internalCommercial.listingFeePaidAt" value={formData.internalCommercial.listingFeePaidAt} onChange={handleInputChange} />
            </Form.Group>
          </div>
          <div className="col-md-4 d-flex align-items-end pb-1">
            <BrandSwitchField
              classes={classes}
              checked={!!formData.internalCommercial.listingFeePaid}
              onChange={(nextChecked) => handleSwitchChange("internalCommercial.listingFeePaid", nextChecked)}
              label="Listing fee paid"
              helperText="Internal finance marker for Lusuku staff."
            />
          </div>
          <div className="col-md-4 d-flex align-items-end pb-1">
            <BrandSwitchField
              classes={classes}
              checked={!!formData.isAIShoppable}
              onChange={(nextChecked) => handleSwitchChange("isAIShoppable", nextChecked)}
              label="AI Shoppable"
              helperText="Live now. Only brands with this enabled are exposed to Lusuku's AI catalog and agent flows."
            />
          </div>
          <div className="col-12">
            <Form.Group>
              <Form.Label className="bm-label">Special Terms</Form.Label>
              <Form.Control className="bm-control" size="sm" name="internalCommercial.specialTerms" value={formData.internalCommercial.specialTerms} onChange={handleInputChange} placeholder="Any agreed special contract terms" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group>
              <Form.Label className="bm-label">Onboarding Notes</Form.Label>
              <Form.Control className="bm-control" size="sm" as="textarea" rows={2} name="internalCommercial.onboardingNotes" value={formData.internalCommercial.onboardingNotes} onChange={handleInputChange} />
            </Form.Group>
          </div>
        </div>
      </FormSection>
    )}

    {/* ── Media ── */}
    <FormSection id="sec-media" icon={FaImage} title="Brand Media" subtitle="Logo, banner & shop banner">
      <div className="row g-3">
        {/* Logo */}
        <div className="col-md-6">
          <Form.Label className="bm-label">Brand Profile Logo <span className="text-danger">*</span></Form.Label>
          <div className="bm-media-spec" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
            <strong>Exact required dimensions: 512×512 px (1:1).</strong> Other dimensions are rejected. Used as the brand’s profile identity beside its name and wherever a compact logo is needed. A transparent PNG with breathing room around the mark works best. Max 2 MB.
          </div>
          <Button
            type="button"
            className="bm-btn-primary w-100 mb-2"
            size="sm"
            onClick={handleLogoUpload}
            disabled={uploadingLogo || removingMedia.logo}
            style={formSubmitAttempted && !formData.imageUrl ? { outline: "2px solid #dc2626", outlineOffset: 2 } : {}}
          >
            {uploadingLogo ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Uploading… {logoUploadProgress}%</> : <><FaUpload className="me-2" />{formData.imageUrl ? "Change Logo" : "Upload Logo"}</>}
          </Button>
          {!formData.imageUrl && (
            <div style={{ fontSize: "0.7rem", color: formSubmitAttempted ? "#dc2626" : "#6b7280", fontWeight: formSubmitAttempted ? 600 : 400 }}>
              {formSubmitAttempted ? "⚠ Logo is required — upload one to continue." : "Upload an exact 512×512 px square logo."}
            </div>
          )}
          {formData.imageUrl && (
            <div className="bm-img-preview">
              <img src={formData.imageUrl} alt="Logo" style={{ width: 56, height: 56, objectFit: "contain", borderRadius: 6, border: "1px solid #e2e8f0", background: "white", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <ImageValidationSummary validation={logoValidation} />
                <Button type="button" variant="outline-danger" size="sm" onClick={handleRemoveLogo} disabled={removingMedia.logo} style={{ marginTop: 6, fontSize: "0.7rem", padding: "1px 8px" }}>
                  {removingMedia.logo ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Removing…</> : <><FaTrash className="me-1" />Remove</>}
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* Landing/card image */}
        <div className="col-md-6">
          <Form.Label className="bm-label">Landing Page Brand Card Image</Form.Label>
          <div className="bm-media-spec">
            <strong>Exact required dimensions: 1280×720 px (16:9).</strong> Other dimensions are rejected. Used only in Featured Brands and other brand cards. The original is stored unchanged and responsive CDN copies are served to shoppers. PNG is recommended for artwork containing text. JPG/PNG/WebP, max 5 MB.
          </div>
          <Button type="button" className="bm-btn-primary w-100 mb-2" size="sm" onClick={handleBannerUpload} disabled={uploadingBanner || removingMedia.banner}>
            {uploadingBanner ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Uploading… {bannerUploadProgress}%</> : <><FaUpload className="me-2" />{formData.bannerUrl ? "Change Card Image" : "Upload Card Image"}</>}
          </Button>
          {formData.bannerUrl && (
            <div className="bm-media-preview">
              <div className="bm-media-frame bm-media-frame--card">
                <img src={formData.bannerUrl} alt="Landing page brand card preview" />
              </div>
              <div className="bm-media-caption">Exact 16:9 preview of the image area used by the landing-page brand card.</div>
              <ImageValidationSummary validation={bannerValidation} />
              <Button type="button" variant="outline-danger" size="sm" onClick={handleRemoveBanner} disabled={removingMedia.banner} style={{ width: "100%", marginTop: 6, fontSize: "0.7rem", padding: "2px 8px" }}>
                {removingMedia.banner ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Removing…</> : <><FaTrash className="me-1" />Remove Card Image</>}
              </Button>
            </div>
          )}
          {!formData.bannerUrl && (
            <div className="bm-media-empty">
              <FaImage />
              <span>No landing-page card image is configured for this brand.</span>
            </div>
          )}
        </div>
        {/* Web Shop Banner */}
        <div className="col-12">
          <Form.Label className="bm-label">
            Web Shop Header Banner
          </Form.Label>
          <div className="bm-media-spec" style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
            <strong>Exact required dimensions: 1500×400 px (15:4).</strong> Other dimensions are rejected. Used as the first slide across the top of the web brand shop; published featured artwork follows it in the carousel. The original is stored unchanged; responsive, non-upscaled CDN copies are served to shoppers. PNG is recommended for crisp text. Max 5 MB.
          </div>
          <Button type="button" className="bm-btn-primary w-100 mb-2" size="sm" onClick={handleShopBannerUpload} disabled={uploadingShopBanner || removingMedia.shopBanner}>
            {uploadingShopBanner ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Uploading… {shopBannerUploadProgress}%</> : <><FaUpload className="me-2" />{formData.shopBannerUrl ? "Change Shop Banner" : "Upload Shop Banner"}</>}
          </Button>
          {formData.shopBannerUrl && (
            <div className="bm-media-preview" style={{ borderColor: "#bfdbfe", background: "#f0f9ff" }}>
              <div className="bm-media-frame bm-media-frame--shop" style={{ borderColor: "#93c5fd" }}>
                <img src={formData.shopBannerUrl} alt="Web shop header banner preview" />
              </div>
              <div className="bm-media-caption">Exact 15:4 preview of the first banner shown in the web shop carousel.</div>
              <ImageValidationSummary validation={shopBannerValidation} />
              <Button type="button" variant="outline-danger" size="sm" onClick={handleRemoveShopBanner} disabled={removingMedia.shopBanner} style={{ width: "100%", marginTop: 6, fontSize: "0.7rem", padding: "2px 8px" }}>
                {removingMedia.shopBanner ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Removing…</> : <><FaTrash className="me-1" />Remove Shop Banner</>}
              </Button>
            </div>
          )}
          {!formData.shopBannerUrl && (
            <div className="bm-media-empty">
              <FaImage />
              <span>No dedicated shop banner is configured. The shop uses the card image when available, then a branded colour fallback.</span>
            </div>
          )}
        </div>

        {/* Additional carousel artwork */}
        <div className="col-12">
          <Form.Label className="bm-label">Additional Shop Carousel Artwork</Form.Label>
          <div className="bm-media-spec" style={{ background: "#f5f3ff", borderColor: "#ddd6fe" }}>
            The shop banner above is the primary slide. New editions add a dedicated 15:4 web slide plus a 16:9 mobile slide. Historical 16:9-only artwork keeps its safe web fallback until replaced.
          </div>
          <div className="bm-carousel-status">
            <span className="bm-carousel-order">
              {formData.shopBannerUrl
                ? "Slide 1 · dedicated shop banner"
                : formData.bannerUrl
                  ? "Slide 1 · card image fallback"
                  : "No primary banner configured"}
            </span>
            <Badge bg="light" text="dark">
              {carouselArtwork.length} additional slide{carouselArtwork.length === 1 ? "" : "s"}
            </Badge>
          </div>

          {loadingBrandContent ? (
            <div className="bm-media-empty">
              <Spinner animation="border" size="sm" />
              <span>Reading saved carousel content…</span>
            </div>
          ) : brandContentError ? (
            <div className="bm-media-empty" style={{ borderColor: "#fecaca", color: "#b91c1c", background: "#fef2f2" }}>
              <FaExclamationTriangle />
              <span>{brandContentError}</span>
            </div>
          ) : carouselArtwork.length > 0 ? (
            <div className="bm-carousel-grid">
              {carouselArtwork.map((artwork, index) => {
                const hasPrimary = !!(formData.shopBannerUrl || formData.bannerUrl);
                const slideNumber = index + (hasPrimary ? 2 : 1);
                const publicationDate = artwork.publicationDate
                  ? new Date(artwork.publicationDate).toLocaleDateString()
                  : "Published artwork";
                const mobileArtworkUrl =
                  artwork.mobileImageUrl || artwork.featuredImageUrl || "";
                const webArtworkUrl = artwork.webImageUrl || "";
                const previewUrl = webArtworkUrl || mobileArtworkUrl;
                const channelLabel =
                  webArtworkUrl && mobileArtworkUrl
                    ? "Web + mobile edition"
                    : webArtworkUrl
                      ? "Web-only edition"
                      : "Mobile-only edition";
                return (
                  <article className="bm-carousel-artwork" key={artwork._id || previewUrl}>
                    <img src={previewUrl} alt={artwork.caption || `Carousel slide ${slideNumber}`} />
                    <div className="bm-carousel-artwork-copy">
                      <strong>Slide {slideNumber}</strong>
                      {artwork.caption || publicationDate}
                      <span>{channelLabel}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="bm-media-empty">
              <FaImage />
              <span>No additional carousel artwork has been published for this brand.</span>
            </div>
          )}

          <Button
            type="button"
            className="bm-btn-secondary w-100 mt-2"
            variant="outline-primary"
            size="sm"
            onClick={onManageCarouselArtwork}
            disabled={!selectedBrandId || loadingBrandContent}
          >
            <FaUpload className="me-2" />
            {selectedBrandId ? "Upload or manage carousel artwork" : "Create the brand before adding carousel artwork"}
          </Button>
        </div>
      </div>
    </FormSection>
  </>
);

// ── Main component ─────────────────────────────────────────────────────────
/* What a brand search looks at: the names an operator knows it by, where it
   is, its email and its phone. Phone numbers match on digits with the leading
   0 or country code dropped, so "0779 991454" finds "+256779991454". */
const phoneKey = (value) =>
  String(value || "").replace(/\D/g, "").replace(/^(256|260|0)/, "");

const brandMatchesQuery = (brand, rawQuery) => {
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const text = [brand.name, brand.tradingName, brand.address, brand.email]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (text.includes(q)) return true;
  const digits = phoneKey(q);
  return digits.length >= 3 && phoneKey(brand.phone).includes(digits);
};

const BrandManagement = () => {
  const classes = useStyles();
  const history = useHistory();
  const { mapsApiLoaded } = useGoogleMaps();
  // Active market drives Places autocomplete bias (country + map center) so the
  // brand/outlet address search is scoped to the country the operator is viewing.
  const { market } = useCountry();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOutletModal, setShowOutletModal] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [carouselArtwork, setCarouselArtwork] = useState([]);
  const [loadingBrandContent, setLoadingBrandContent] = useState(false);
  const [brandContentError, setBrandContentError] = useState("");
  const editBrandRequestRef = useRef(0);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toggleLoading, setToggleLoading] = useState({});
  const [brandHours, setBrandHours] = useState(null);
  const [isSavingHours, setIsSavingHours] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingShopBanner, setUploadingShopBanner] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [shopBannerUploadProgress, setShopBannerUploadProgress] = useState(0);
  const [logoValidation, setLogoValidation] = useState({ dimensions: null, format: null, size: null });
  const [bannerValidation, setBannerValidation] = useState({ dimensions: null, format: null, size: null });
  const [shopBannerValidation, setShopBannerValidation] = useState({ dimensions: null, format: null, size: null });
  const [removingMedia, setRemovingMedia] = useState({});
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  /* Persisted: this used to be useState(false), so the switch reset on every
     reload and an operator working through inactive brands had to re-flick it
     each time. See hooks/usePersistedToggle.js. */
  const [showInactive, setShowInactive] = usePersistedToggle(
    "brandManagement.showInactive",
    false
  );
  const [brandQuery, setBrandQuery] = useState("");
  const [featuredBrandsActive, setFeaturedBrandsActive] = useState(true);
  /* What the media-delete confirmation is currently asking about.
     null = closed. Holds the whole request so the dialog can name the exact
     asset — "Delete shop banner?" not "Are you sure?". */
  const [mediaConfirm, setMediaConfirm] = useState(null);
  const [isUpdatingFeaturedToggle, setIsUpdatingFeaturedToggle] = useState(false);
  const [ownerInviteLoading, setOwnerInviteLoading] = useState({});
  const [emailValidation, setEmailValidation] = useState({ status: "idle", message: "", code: null, checking: false });

  const [formData, setFormData] = useState({
    name: "", tradingName: "", description: "", address: "",
    latitude: null, longitude: null, deliveryZoneRadius: 5,
    phone: "", email: "", imageUrl: "", bannerUrl: "", shopBannerUrl: "",
    fixedPrice: 0, timezone: "Africa/Kampala", owner: "",
    ...defaultB2BForm(),
  });

  const user = getUserFromLocalStorage();
  const adminId = user?.result?._id;
  const isSuperAdmin = user?.result?.userType === "LUSUKU STAFF" && user?.result?.user_role === "SUPERUSER";
  const canManageBrands =
    isSuperAdmin || (permissionsLoaded && can("brands:manage"));
  const canCreateBrands =
    canManageBrands || (permissionsLoaded && can("brands:create"));
  const canUpdateBrands =
    canManageBrands || (permissionsLoaded && can("brands:update"));
  const canDeleteBrands =
    canManageBrands || (permissionsLoaded && can("brands:delete"));
  const canApproveBrands =
    canManageBrands || (permissionsLoaded && can("brands:approve"));
  const canViewDirectory =
    user?.result?.userType === "LUSUKU STAFF" &&
    (isSuperAdmin || !permissionsLoaded || can("brands:read"));

  useEffect(() => {
    fetchBrands();
  }, [showInactive, canViewDirectory]);

  // ── Brand owner email pre-validation (debounced) ─────────────────────────
  useEffect(() => {
    const email = (formData.email || "").trim();
    const isFormOpen = showCreateModal || showEditModal;
    if (!isFormOpen) return undefined;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailValidation({ status: "idle", message: "", code: null, checking: false });
      return undefined;
    }

    setEmailValidation((prev) => ({ ...prev, checking: true }));
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const brandId = showEditModal ? selectedBrand?._id : undefined;
        const response = await apiCheckBrandOwnerEmail({ email, brandId });
        if (cancelled) return;
        const { status, message, code } = response.data || {};
        setEmailValidation({
          status: status || "idle",
          message: message || "",
          code: code || null,
          checking: false,
        });
      } catch (err) {
        if (cancelled) return;
        setEmailValidation({ status: "idle", message: "", code: null, checking: false });
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [formData.email, showCreateModal, showEditModal, selectedBrand?._id]);

  // ── Data fetch ───────────────────────────────────────────────────────────
  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = canViewDirectory
        ? await apiFetchBrandDirectory({ includeInactive: showInactive })
        : await apiFetchBrands();
      const { data, featuredBrandsActive: featuredActive } = response.data || {};
      setBrands(data || []);
      if (typeof featuredActive === "boolean") setFeaturedBrandsActive(featuredActive);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch brands");
      toast.error("Failed to fetch brands");
    } finally {
      setLoading(false);
    }
  };

  const handleFeaturedBrandsToggle = async () => {
    if (!canManageBrands || isUpdatingFeaturedToggle) return;
    const nextValue = !featuredBrandsActive;
    setFeaturedBrandsActive(nextValue);
    setIsUpdatingFeaturedToggle(true);
    try {
      const response = await apiUpdateFeaturedBrandsSetting({
        isActive: nextValue,
        updatedBy: adminId,
      });
      const payload = response.data || {};
      if (typeof payload.featuredBrandsActive === "boolean") setFeaturedBrandsActive(payload.featuredBrandsActive);
      toast.success(`Featured brands carousel ${nextValue ? "enabled" : "hidden"} on landing page`);
    } catch (err) {
      setFeaturedBrandsActive((prev) => !prev);
      toast.error(err.response?.data?.message || "Failed to update featured brands carousel toggle");
    } finally {
      setIsUpdatingFeaturedToggle(false);
    }
  };

  // ── Input handlers ────────────────────────────────────────────────────────
  const updateFormValue = (name, nextValue) => {
    const [group, key] = name.split(".");

    if (key && ["internalCommercial", "fulfilment", "promotion", "dashboardPrefs"].includes(group)) {
      setFormData((prev) => ({
        ...prev,
        [group]: {
          ...prev[group],
          [key]: nextValue,
        },
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateFormValue(name, type === "checkbox" ? checked : value);
  };

  const handleSwitchChange = (name, checked) => {
    updateFormValue(name, checked);
  };

  const toggleFlowFlag = (key) => setFormData((prev) => ({ ...prev, flows: { ...prev.flows, [key]: !prev.flows[key] } }));

  // ── Payload builder ───────────────────────────────────────────────────────
  const buildBrandPayload = (forCreate) => {
    const payload = {
      ...formData,
      fixedPrice: formData.fixedPrice === "" || formData.fixedPrice == null ? 0 : Number(formData.fixedPrice),
      fulfilment: {
        ...formData.fulfilment,
        leadTimeStandardDays: Number(formData.fulfilment.leadTimeStandardDays) || 0,
        leadTimeCustomDays: Number(formData.fulfilment.leadTimeCustomDays) || 0,
        minimumOrderValueUgx: Number(formData.fulfilment.minimumOrderValueUgx) || 0,
      },
      internalCommercial: isSuperAdmin ? {
        ...formData.internalCommercial,
        commissionRateAgreed: formData.internalCommercial.commissionRateAgreed ? Number(formData.internalCommercial.commissionRateAgreed) : undefined,
        onboardingDate: formData.internalCommercial.onboardingDate ? new Date(formData.internalCommercial.onboardingDate) : undefined,
        listingFeePaidAt: formData.internalCommercial.listingFeePaidAt ? new Date(formData.internalCommercial.listingFeePaidAt) : undefined,
        accountManagerUserId: formData.internalCommercial.accountManagerUserId || undefined,
      } : undefined,
    };
    if (payload.flows?.customQuoteConfig) {
      const rawFields = payload.flows.customQuoteConfig.fields || [];
      payload.flows = {
        ...payload.flows,
        customQuoteConfig: {
          introText: payload.flows.customQuoteConfig.introText || "",
          fields: rawFields.filter((f) => f.label && f.label.trim()).map((f, i) => ({
            fieldKey: f.fieldKey || f.label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""),
            label: f.label.trim(),
            fieldType: f.fieldType || "text",
            required: !!f.required,
            placeholder: f.placeholder || "",
            options: typeof f.options === "string" ? f.options.split(",").map((o) => o.trim()).filter(Boolean) : (f.options || []),
            helpText: f.helpText || "",
            order: i,
          })),
        },
      };
    }
    // Normalise productSpecTemplate before save: drop blank rows and
    // split comma-separated option strings into arrays.
    if (payload.productSpecTemplate) {
      const rawSpecFields = payload.productSpecTemplate.fields || [];
      payload.productSpecTemplate = {
        enabled: !!payload.productSpecTemplate.enabled,
        introText: payload.productSpecTemplate.introText || "",
        fields: rawSpecFields
          .filter((f) => f.label && f.label.trim())
          .map((f, i) => ({
            fieldKey:
              f.fieldKey ||
              f.label
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, ""),
            label: f.label.trim(),
            fieldType: f.fieldType || "text",
            required: !!f.required,
            buyerConfigurableDefault: !!f.buyerConfigurableDefault,
            displayOnCard: !!f.displayOnCard,
            unit: f.unit || "",
            placeholder: f.placeholder || "",
            options:
              typeof f.options === "string"
                ? f.options.split(",").map((o) => o.trim()).filter(Boolean)
                : f.options || [],
            helpText: f.helpText || "",
            order: i,
          })),
      };
    }
    if (forCreate) {
      if (formData.owner && String(formData.owner).trim()) {
        payload.owner = String(formData.owner).trim();
      } else {
        delete payload.owner;
      }
    } else {
      delete payload.owner;
    }
    if (!isSuperAdmin) delete payload.internalCommercial;
    return payload;
  };

  // ── Image validation gate (called before any save) ────────────────────────
  const checkImageValidation = () => {
    if (logoValidation.dimensions && !logoValidation.dimensions.isValid) {
      toast.error(`Logo dimensions are invalid — ${logoValidation.dimensions.message}. Please re-upload.`);
      return false;
    }
    if (logoValidation.size && !logoValidation.size.isValid) {
      toast.error("Logo file is too large. Maximum size is 2 MB.");
      return false;
    }
    if (bannerValidation.dimensions && !bannerValidation.dimensions.isValid) {
      toast.error(`Banner dimensions are invalid — ${bannerValidation.dimensions.message}. Please re-upload.`);
      return false;
    }
    if (bannerValidation.size && !bannerValidation.size.isValid) {
      toast.error("Banner file is too large. Maximum size is 5 MB.");
      return false;
    }
    return true;
  };

  // ── CRUD handlers ─────────────────────────────────────────────────────────
  const isEmailHardConflict =
    emailValidation.status === "staff_conflict" ||
    emailValidation.status === "owned_by_other_brand" ||
    emailValidation.status === "buyer_account_conflict";

  const handleBrandSubmitError = (err, fallbackMessage) => {
    const data = err?.response?.data || {};
    if (err?.response?.status === 409 && data.field === "email") {
      setEmailValidation({
        status: data.code === "EMAIL_BELONGS_TO_STAFF" ? "staff_conflict"
          : data.code === "EMAIL_OWNS_OTHER_BRAND" ? "owned_by_other_brand"
          : data.code === "EMAIL_BELONGS_TO_BUYER" ? "buyer_account_conflict"
          : "staff_conflict",
        message: data.message || "That email is already in use.",
        code: data.code || null,
        checking: false,
      });
      toast.error(data.message || "That email cannot be used.");
      return;
    }
    toast.error(data.message || fallbackMessage);
  };

  // Address is entered via Google Places autocomplete (no native `required`
  // input to lean on), so gate submit on a selected address WITH coordinates —
  // selecting a suggestion is what geocodes and captures lat/lng.
  const isBrandAddressValid = () => {
    if (!formData.address || !formData.address.trim()) {
      toast.error("Address is required. Search and select it from the suggestions.");
      return false;
    }
    if (!formData.latitude || !formData.longitude) {
      toast.error("Please select the address from the dropdown suggestions so we can capture its GPS coordinates.");
      return false;
    }
    return true;
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!canCreateBrands) return;
    setFormSubmitAttempted(true);
    if (!isBrandAddressValid()) return;
    // Require logo on create
    if (!formData.imageUrl) {
      toast.error("A brand logo is required. Please upload one before saving.");
      return;
    }
    if (!checkImageValidation()) return;
    if (isEmailHardConflict) {
      toast.error(emailValidation.message || "Resolve the email conflict before saving.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiCreateBrand(buildBrandPayload(true));
      const provisioning = response.data?.ownerProvisioning;
      toast.success(response.data?.message || "Brand created successfully!");
      if (provisioning?.message) {
        toast.info(provisioning.message, { autoClose: 9000 });
      }
      setShowCreateModal(false);
      sessionUploadsRef.current.clear(); // uploads are now persisted, not orphans
      resetForm();
      fetchBrands();
    } catch (err) {
      handleBrandSubmitError(err, "Failed to create brand");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditBrand = async (e) => {
    e.preventDefault();
    if (!canUpdateBrands) return;
    setFormSubmitAttempted(true);
    if (!isBrandAddressValid()) return;
    if (!formData.imageUrl) {
      toast.error("A brand logo is required. Please upload one before saving.");
      return;
    }
    if (!checkImageValidation()) return;
    if (isEmailHardConflict) {
      toast.error(emailValidation.message || "Resolve the email conflict before saving.");
      return;
    }
    setIsSubmitting(true);
    try {
      await apiUpdateBrand(selectedBrand._id, buildBrandPayload(false));
      toast.success("Brand updated successfully!");
      setShowEditModal(false);
      sessionUploadsRef.current.clear(); // uploads are now persisted, not orphans
      resetForm();
      fetchBrands();
    } catch (err) {
      handleBrandSubmitError(err, "Failed to update brand");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBrand = async () => {
    if (!brandToDelete || !canDeleteBrands) return;
    setIsSubmitting(true);
    try {
      await apiDeleteBrand(brandToDelete);
      toast.success("Brand deleted successfully!");
      setShowDeleteModal(false);
      setBrandToDelete(null);
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete brand");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (brandId, currentStatus) => {
    if (!canApproveBrands) return;
    setToggleLoading((prev) => ({ ...prev, [brandId]: true }));
    try {
      await apiToggleBrandStatus(brandId);
      toast.success(`Brand ${currentStatus ? "deactivated" : "activated"} successfully!`);
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to toggle status");
    } finally {
      setToggleLoading((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  const handleUpdateBrand = async (brandId, updateData) => {
    if (!canUpdateBrands) return;
    setToggleLoading((prev) => ({ ...prev, [brandId]: true }));
    try {
      await apiUpdateBrand(brandId, updateData);
      toast.success("Brand updated successfully!");
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update brand");
    } finally {
      setToggleLoading((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  // Only a super admin may send this: it delivers a live set-password link for
  // somebody else's account. The server enforces it (isSuperUser); this gate
  // just stops the button being offered to people who would get a 403.
  const handleResendOwnerSetupLink = async (brand) => {
    if (!isSuperAdmin) return;
    setOwnerInviteLoading((prev) => ({ ...prev, [brand._id]: true }));
    try {
      const response = await apiResendBrandOwnerSetupLink(brand._id);
      // Report the address the SERVER resolved, not the row's business email —
      // the two are routinely different.
      toast.success(
        response.data?.sentTo
          ? `Sign-in details sent to ${response.data.sentTo}.`
          : response.data?.message || "Sent the owner setup link."
      );
    } catch (err) {
      const code = String(err.response?.data?.code || "");
      const message = err.response?.data?.message || "Failed to resend owner setup link";
      // Point at the way round it when the failure is the mail itself or a
      // missing address: the green lock button beside this one.
      const hint = /^(MAIL_|EMAIL_SEND_FAILED$)/.test(code) || code === "VENDOR_HAS_NO_EMAIL"
        ? " You can use the green lock button to set a password and send it yourself."
        : "";
      toast.error(message + hint);
    } finally {
      setOwnerInviteLoading((prev) => ({ ...prev, [brand._id]: false }));
    }
  };

  // The shared outlet form (components/BrandOutletFormModal) validates and
  // builds the body, and toasts a failed request; this only persists and
  // refreshes the outlet counts.
  const handleCreateOutlet = async (body) => {
    if (!selectedBrand || !canCreateBrands) return;
    setIsSubmitting(true);
    try {
      await apiCreateBrandOutlet(selectedBrand._id, body);
      toast.success("Outlet created successfully!");
      setShowOutletModal(false);
      fetchBrands();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManageHours = (brand) => {
    if (!canUpdateBrands) return;
    setSelectedBrand(brand);
    setBrandHours(brand.openHours || null);
    setShowHoursModal(true);
  };

  const handleSaveHours = async (hours, timezone) => {
    if (!selectedBrand || !canUpdateBrands) return;
    setIsSavingHours(true);
    try {
      // Was `apiUpdateOpenHours({ type, id, hours })` — a single object into a
      // two-arg (vendorId, payload) helper, which produced
      // `PATCH /restaurants/[object Object]/open-hours` with an undefined body.
      // Brand hours have never saved.
      await apiUpdateVendorOpenHours("brand", selectedBrand._id, {
        openHours: hours,
        ...(timezone ? { timezone } : {}),
      });
      toast.success("Operating hours updated successfully!");
      setShowHoursModal(false);
      fetchBrands();
    } catch (err) {
      toast.error("Failed to update operating hours");
    } finally {
      setIsSavingHours(false);
    }
  };

  const resetForm = () => {
    editBrandRequestRef.current += 1;
    setFormData({
      name: "", tradingName: "", description: "", address: "",
      latitude: null, longitude: null, deliveryZoneRadius: 5,
      phone: "", email: "", imageUrl: "", bannerUrl: "", shopBannerUrl: "",
      fixedPrice: 0, timezone: "Africa/Kampala", owner: "",
      ...defaultB2BForm(),
    });
    setSelectedBrand(null);
    setCarouselArtwork([]);
    setLoadingBrandContent(false);
    setBrandContentError("");
    setLogoValidation({ dimensions: null, format: null, size: null });
    setBannerValidation({ dimensions: null, format: null, size: null });
    setShopBannerValidation({ dimensions: null, format: null, size: null });
    setEmailValidation({ status: "idle", message: "", code: null, checking: false });
    setFormSubmitAttempted(false);
    setUploadingLogo(false);
    setUploadingBanner(false);
    setUploadingShopBanner(false);
    setLogoUploadProgress(0);
    setBannerUploadProgress(0);
    setShopBannerUploadProgress(0);
    setRemovingMedia({});
  };

  const openEditModal = async (brand) => {
    if (!canUpdateBrands) return;
    const requestId = editBrandRequestRef.current + 1;
    editBrandRequestRef.current = requestId;
    setSelectedBrand(brand);
    setFormData(brandToFormData(brand));
    setCarouselArtwork([]);
    setBrandContentError("");
    setLoadingBrandContent(true);
    setShowEditModal(true);

    const [brandResult, artworkResult] = await Promise.allSettled([
      apiFetchBrandById(brand._id),
      apiFetchBrandFeaturedImageHistory(brand._id),
    ]);
    if (editBrandRequestRef.current !== requestId) return;

    if (brandResult.status === "fulfilled") {
      const detailedBrand = brandResult.value?.data?.data || {};
      // The privileged directory carries internalCommercial for permitted
      // staff. Preserve it if the general detail endpoint intentionally omits
      // it for this viewer while still refreshing every public/config field.
      const hydratedBrand = {
        ...brand,
        ...detailedBrand,
        internalCommercial:
          detailedBrand.internalCommercial || brand.internalCommercial,
      };
      setSelectedBrand(hydratedBrand);
      setFormData(brandToFormData(hydratedBrand));
      setBrands((current) =>
        current.map((item) =>
          item._id === hydratedBrand._id ? { ...item, ...hydratedBrand } : item
        )
      );
    } else {
      toast.warn(
        "The full brand profile could not be refreshed. Showing the saved directory values instead."
      );
    }

    if (artworkResult.status === "fulfilled") {
      setCarouselArtwork(artworkResult.value?.data?.data || []);
    } else {
      setCarouselArtwork([]);
      setBrandContentError(
        artworkResult.reason?.response?.data?.message ||
          "Saved carousel artwork could not be read. Retry by reopening this brand."
      );
    }
    setLoadingBrandContent(false);
  };

  const openOutletModal = (brand) => {
    if (!canCreateBrands) return;
    setSelectedBrand(brand);
    setShowOutletModal(true);
  };

  const openDeleteModal = (brandId) => {
    if (!canDeleteBrands) return;
    setBrandToDelete(brandId);
    setShowDeleteModal(true);
  };

  // ── Cloudinary session-upload tracking (pre-save orphan cleanup) ──────────
  // URLs uploaded during THIS edit session. If one is discarded (removed,
  // replaced, or the modal is cancelled) before Save, we reclaim it from
  // Cloudinary right away. Images that WERE saved are reclaimed server-side by
  // the brand update-diff instead, so a plain Cancel never destroys a live asset.
  const sessionUploadsRef = useRef(new Set());
  const cleanupIfSessionUpload = async (url) => {
    if (!url || !sessionUploadsRef.current.has(url)) return false;
    const response = await apiCleanupBrandMedia(url);
    if (!response.data?.deleted) {
      throw new Error("Cloudinary could not confirm deletion of the unsaved image.");
    }
    sessionUploadsRef.current.delete(url);
    return true;
  };
  const discardSessionUploads = async () => {
    const urls = Array.from(sessionUploadsRef.current);
    await Promise.allSettled(urls.map((url) => cleanupIfSessionUpload(url)));
  };

  // ── Image validation helpers ──────────────────────────────────────────────
  const validateImageDimensions = (width, height, type) => {
    const rule = BRAND_MEDIA_RULES[type];
    const isValid = width === rule.width && height === rule.height;
    return {
      isValid,
      isOptimal: isValid,
      message: isValid
        ? `Exact (${width}×${height}px, ${rule.ratioLabel})`
        : `Rejected: expected exactly ${rule.width}×${rule.height}px; received ${width}×${height}px`,
    };
  };

  const validateFormat = (format) => ["jpg", "jpeg", "png", "webp"].includes(String(format || "").toLowerCase());
  const validateSize = (size, type) => size <= (type === "logo" ? 2000000 : 5000000);

  // ── Exact, signed brand-media upload ──────────────────────────────────────
  const readLocalImageDimensions = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected file could not be read as an image."));
    };
    img.src = objectUrl;
  });

  const uploadSignedBrandMedia = (file, signatureData, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(
        "POST",
        `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`,
        true
      );
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        try {
          const result = JSON.parse(xhr.responseText || "{}");
          if (xhr.status >= 200 && xhr.status < 300) resolve(result);
          else reject(new Error(result?.error?.message || "Cloudinary upload failed."));
        } catch {
          reject(new Error("Cloudinary returned an invalid upload response."));
        }
      };
      xhr.onerror = () => reject(new Error("Network error during image upload."));
      xhr.ontimeout = () => reject(new Error("Image upload timed out."));
      xhr.timeout = 60000;

      const body = new FormData();
      body.append("file", file, file.name);
      body.append("api_key", signatureData.apiKey);
      body.append("timestamp", String(signatureData.timestamp));
      body.append("folder", signatureData.folder);
      body.append("public_id", signatureData.publicId);
      body.append("upload_preset", signatureData.uploadPreset);
      body.append("signature", signatureData.signature);
      xhr.send(body);
    });

  const openBrandMediaPicker = (type) => {
    if (!formData.name) {
      toast.error("Please enter the brand name first");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const isLogo = type === "logo";
      const isShopBanner = type === "shopBanner";
      const label = isLogo ? "Logo" : isShopBanner ? "Shop Banner" : "Card Image";
      const fieldMap = { logo: "imageUrl", banner: "bannerUrl", shopBanner: "shopBannerUrl" };
      const setUploading = { logo: setUploadingLogo, banner: setUploadingBanner, shopBanner: setUploadingShopBanner };
      const setProgress = { logo: setLogoUploadProgress, banner: setBannerUploadProgress, shopBanner: setShopBannerUploadProgress };
      const setValidation = { logo: setLogoValidation, banner: setBannerValidation, shopBanner: setShopBannerValidation };
      const sourceFormat = String(file.type || "").split("/")[1] || "";

      try {
        const { width, height } = await readLocalImageDimensions(file);
        const dimensions = validateImageDimensions(width, height, type);
        const formatIsValid = validateFormat(sourceFormat);
        const sizeIsValid = validateSize(file.size, type);
        const sourceValidation = {
          dimensions,
          format: {
            isValid: formatIsValid,
            message: formatIsValid ? `Source format: ${sourceFormat.toUpperCase()}` : "Invalid format",
          },
          size: {
            isValid: sizeIsValid,
            message: sizeIsValid ? "Source size OK" : `File too large (max ${isLogo ? "2MB" : "5MB"})`,
          },
        };
        setValidation[type](sourceValidation);

        if (!dimensions.isValid || !formatIsValid || !sizeIsValid) {
          toast.error(`${label} not accepted. ${dimensions.message}`);
          return;
        }

        setUploading[type](true);
        setProgress[type](0);
        const signatureResponse = await apiCreateBrandMediaUploadSignature({
          type,
          brandName: formData.name,
        });
        const uploaded = await uploadSignedBrandMedia(
          file,
          signatureResponse.data,
          setProgress[type]
        );

        // Second gate: each dedicated preset must preserve the exact source.
        const uploadedDimensions = validateImageDimensions(uploaded.width, uploaded.height, type);
        const uploadedFormatIsValid = validateFormat(uploaded.format);
        const uploadedSizeIsValid = validateSize(uploaded.bytes, type);
        setValidation[type]({
          dimensions: uploadedDimensions,
          format: {
            isValid: uploadedFormatIsValid,
            message: uploadedFormatIsValid ? `Stored format: ${String(uploaded.format).toUpperCase()}` : "Invalid stored format",
          },
          size: {
            isValid: uploadedSizeIsValid,
            message: uploadedSizeIsValid ? "Stored size OK" : `Stored file too large (max ${isLogo ? "2MB" : "5MB"})`,
          },
        });

        if (!uploadedDimensions.isValid || !uploadedFormatIsValid || !uploadedSizeIsValid) {
          apiCleanupBrandMedia(uploaded.secure_url).catch(() => {});
          throw new Error(`Cloudinary changed the image. ${uploadedDimensions.message}`);
        }

        try {
          await cleanupIfSessionUpload(formData[fieldMap[type]]);
        } catch (cleanupError) {
          await apiCleanupBrandMedia(uploaded.secure_url).catch(() => {});
          throw cleanupError;
        }
        sessionUploadsRef.current.add(uploaded.secure_url);
        setFormData((prev) => ({ ...prev, [fieldMap[type]]: uploaded.secure_url }));
        setProgress[type](100);
        toast.success(`${label} verified and stored unchanged at ${uploaded.width}×${uploaded.height}px.`);
      } catch (error) {
        setProgress[type](0);
        toast.error(error?.response?.data?.message || error.message || `${label} upload failed.`);
      } finally {
        setUploading[type](false);
      }
    };
    input.click();
  };

  const handleLogoUpload = (e) => { e?.preventDefault?.(); openBrandMediaPicker("logo"); };
  const handleBannerUpload = (e) => { e?.preventDefault?.(); openBrandMediaPicker("banner"); };
  const handleShopBannerUpload = (e) => { e?.preventDefault?.(); openBrandMediaPicker("shopBanner"); };
  const MEDIA_FIELD = { logo: "imageUrl", banner: "bannerUrl", shopBanner: "shopBannerUrl" };

  /* Step 1 — decide whether this even needs asking.
     A file picked in this session and not yet saved is a local preview: binning
     it costs nothing and reaches no server, so interrupting for a confirmation
     would be noise. Only an asset already persisted to the brand gets a prompt. */
  const removeBrandMediaFromForm = (type, label, setValidation) => {
    const field = MEDIA_FIELD[type];
    const mediaUrl = formData[field];
    if (!mediaUrl || removingMedia[type]) return;

    const isSessionUpload = sessionUploadsRef.current.has(mediaUrl);
    const isPersisted = !!selectedBrand?._id && !isSessionUpload;
    if (isPersisted) {
      setMediaConfirm({ type, label, setValidation, mediaUrl });
      return;
    }
    performRemoveBrandMedia({ type, label, setValidation, mediaUrl });
  };

  // Step 2 — actually do it.
  const performRemoveBrandMedia = async ({ type, label, setValidation, mediaUrl }) => {
    const field = MEDIA_FIELD[type];
    const isSessionUpload = sessionUploadsRef.current.has(mediaUrl);
    const isPersisted = !!selectedBrand?._id && !isSessionUpload;

    setRemovingMedia((prev) => ({ ...prev, [type]: true }));
    try {
      if (isSessionUpload) {
        await cleanupIfSessionUpload(mediaUrl);
      } else if (isPersisted) {
        await apiRemoveBrandMedia(selectedBrand._id, type);
        setSelectedBrand((prev) => prev ? { ...prev, [field]: "" } : prev);
        setBrands((prev) => prev.map((brand) =>
          brand._id === selectedBrand._id ? { ...brand, [field]: "" } : brand
        ));
      }

      setFormData((prev) => ({ ...prev, [field]: "" }));
      setValidation({ dimensions: null, format: null, size: null });
      toast.success(
        isPersisted
          ? `${label} removed from the brand and Cloudinary.`
          : `${label} removed.`
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error.message ||
        `Could not remove ${label.toLowerCase()}.`
      );
    } finally {
      setRemovingMedia((prev) => ({ ...prev, [type]: false }));
      setMediaConfirm(null);
    }
  };
  const handleRemoveLogo = (e) => { e?.preventDefault?.(); removeBrandMediaFromForm("logo", "Logo", setLogoValidation); };
  const handleRemoveBanner = (e) => { e?.preventDefault?.(); removeBrandMediaFromForm("banner", "Card image", setBannerValidation); };
  const handleRemoveShopBanner = (e) => { e?.preventDefault?.(); removeBrandMediaFromForm("shopBanner", "Shop banner", setShopBannerValidation); };

  const handleManageCarouselArtwork = () => {
    if (!selectedBrand?._id) return;
    if (sessionUploadsRef.current.size > 0) {
      toast.info("Save or remove the newly uploaded brand media before opening the carousel artwork manager.");
      return;
    }
    const brandId = selectedBrand._id;
    setShowEditModal(false);
    resetForm();
    history.push(`/admin/featured-image-upload?brandId=${encodeURIComponent(brandId)}`);
  };

  // ── Shared form props ─────────────────────────────────────────────────────
  const formBodyProps = {
    market, mapsApiLoaded,
    formData, setFormData, handleInputChange, handleSwitchChange, toggleFlowFlag, isSuperAdmin,
    handleLogoUpload, handleBannerUpload, handleShopBannerUpload,
    handleRemoveLogo, handleRemoveBanner, handleRemoveShopBanner,
    uploadingLogo, uploadingBanner, uploadingShopBanner,
    logoUploadProgress, bannerUploadProgress, shopBannerUploadProgress,
    logoValidation, bannerValidation, shopBannerValidation, removingMedia,
    classes, formSubmitAttempted, emailValidation,
    selectedBrandId: selectedBrand?._id || "",
    carouselArtwork,
    loadingBrandContent,
    brandContentError,
    onManageCarouselArtwork: handleManageCarouselArtwork,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading && brands.length === 0) {
    return (
      <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div style={{ textAlign: "center" }}>
          <Spinner animation="grow" style={{ color: BRAND_MID }} />
          <div style={{ marginTop: 12, color: BRAND_DARK, fontWeight: 600 }}>Loading brands…</div>
        </div>
      </div>
    );
  }

  const visibleBrands = brands.filter((brand) => brandMatchesQuery(brand, brandQuery));

  return (
    <div className="content">
      <style>{styles}</style>

      {/* ── Page header ── */}
      <div className="bm-page-header">
        <div>
          <h4>Brand Management</h4>
          <p>{brands.length} brand{brands.length !== 1 ? "s" : ""} {showInactive ? "(including inactive)" : "active"}</p>
        </div>
        <div className="bm-header-controls">
          {(canManageBrands || canCreateBrands || canViewDirectory) && (
            <>
              {canManageBrands && (
              <label className="bm-toggle-pill">
                <Switch
                  checked={featuredBrandsActive}
                  onChange={handleFeaturedBrandsToggle}
                  disabled={isUpdatingFeaturedToggle}
                  size="small"
                  classes={{ switchBase: classes.switchBase, track: classes.track, checked: classes.checked }}
                />
                <span>
                  Landing page Featured Brands
                  {/* The pill is a <label> around the switch, so a click on
                      the help icon would flip a setting every market sees.
                      preventDefault keeps the tap on the tooltip. */}
                  <span onClick={(e) => e.preventDefault()}>
                    <SettingHelp topic="featuredBrandsCarousel" placement="bottom" />
                  </span>
                </span>
                {isUpdatingFeaturedToggle && (
                  /* variant="light": this header is a navy gradient with
                     white text. Without it the global spinner floor in
                     lusuku-overrides.css paints the ring navy on navy and
                     the toggle looks like it did nothing. */
                  <Spinner animation="border" size="sm" variant="light" />
                )}
              </label>
              )}
              {canViewDirectory && (
              <label className="bm-toggle-pill">
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                  classes={{ switchBase: classes.switchBase, track: classes.track, checked: classes.checked }}
                />
                <span>Show Inactive</span>
              </label>
              )}
              {canManageBrands && (
              <Button variant="outline-light" size="sm" style={{ borderRadius: 8, fontWeight: 600 }} onClick={() => history.push("/admin/brand-b2b-quotes")}>
                B2B Quotes
              </Button>
              )}
              {canCreateBrands && (
              <Button
                style={{ background: "white", color: BRAND_DARK, border: "none", borderRadius: 8, fontWeight: 700, padding: "7px 18px" }}
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                + Create Brand
              </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Brands table ── */}
      <Card className="bm-card">
        <Card.Body style={{ padding: 0 }}>
          {error && <div className="alert alert-danger mx-3 mt-3">{error}</div>}
          {brands.length > 0 && (
            <div className="bm-toolbar">
              <SearchBox
                className="bm-search-wrap"
                value={brandQuery}
                onChange={setBrandQuery}
                placeholder="Search brands by name, address or phone"
              />
              {brandQuery.trim() && (
                <SearchHits
                  shown={visibleBrands.length}
                  total={brands.length}
                  noun={brands.length === 1 ? "brand" : "brands"}
                />
              )}
            </div>
          )}
          <div className="bm-table-scroll">
          <Table className="bm-table mb-0">
            <thead>
              <tr>
                <th>Logo</th>
                <th>Name</th>
                <th>Trading Name</th>
                <th className="col-address">Address</th>
                <th>Phone</th>
                <th>Outlets</th>
                <th>Featured</th>
                <th title="Show in Featured Brands carousel on the landing page">On Landing</th>
                <th>Active</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                    <div style={{ width: 60, height: 60, borderRadius: "50%", background: BRAND_LIGHT, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                      <FaStore style={{ fontSize: 24, color: BRAND_MID }} />
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>No brands yet</div>
                    {canCreateBrands && <Button size="sm" className="bm-btn-primary mt-2" onClick={() => setShowCreateModal(true)}>+ Create Brand</Button>}
                  </td>
                </tr>
              ) : (
                visibleBrands.map((brand) => (
                  <tr key={brand._id} style={{ opacity: brand.isActive === false ? 0.6 : 1 }}>
                    <td>
                      <div style={{ width: 60, height: 60, borderRadius: 10, overflow: "hidden", background: "linear-gradient(135deg, #f1f5f9, #dbeafe)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}>
                        {brand.imageUrl
                          ? <img src={brand.imageUrl} alt={brand.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }} onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                          : null}
                        <div style={{ width: "100%", height: "100%", display: brand.imageUrl ? "none" : "flex", alignItems: "center", justifyContent: "center" }}>
                          <FaImage style={{ fontSize: 22, color: "#94a3b8" }} />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, textDecoration: brand.isActive === false ? "line-through" : "none", color: brand.isActive === false ? "#9ca3af" : BRAND_DARK }}>{brand.name}</div>
                      {/* Brands are created deactivated and go live only when a
                          super admin activates them. Without this split, a brand
                          still waiting for approval and a brand that was
                          deliberately pulled are the same greyed-out row.
                          `activatedAt` is null until the first activation. */}
                      {brand.isActive === false && (
                        brand.activatedAt
                          ? <span className="bm-badge-state bm-badge-state--off">Deactivated</span>
                          : <span className="bm-badge-state bm-badge-state--pending">Pending activation</span>
                      )}
                      {brand.isAIShoppable && <span className="bm-badge-ai"><FaRobot style={{ marginRight: 3 }} />AI</span>}
                    </td>
                    <td style={{ color: "#6b7280" }}>{brand.tradingName || "—"}</td>
                    <td className="col-address">{brand.address || "—"}</td>
                    <td style={{ fontSize: "0.85rem", color: "#374151" }}>{brand.phone || "—"}</td>
                    <td>
                      <Badge
                        bg={brand.outlets?.length > 0 ? "primary" : "secondary"}
                        style={{ cursor: "pointer", fontSize: "0.78rem", padding: "5px 10px", borderRadius: 20 }}
                        onClick={() => (brand.outlets?.length > 0 || canCreateBrands) && history.push("/admin/brand-outlet-management", { brandId: brand._id, brandName: brand.name })}
                        title={brand.outlets?.length > 0 ? `Manage ${brand.outlets.length} outlet(s)` : "Add outlets"}
                      >
                        {brand.outlets?.length || 0} Outlet{brand.outlets?.length !== 1 ? "s" : ""}
                      </Badge>
                    </td>
                    <td>
                      <Switch
                        checked={brand.featured === true}
                        onChange={() => handleUpdateBrand(brand._id, { ...brand, featured: !brand.featured })}
                        disabled={!canUpdateBrands || toggleLoading[brand._id]}
                        size="small"
                        classes={{ switchBase: classes.switchBase, track: classes.track, checked: classes.checked }}
                        color="primary"
                      />
                    </td>
                    <td>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{brand.promotion?.promoteOnLanding ? "Remove from landing page carousel" : "Show in landing page carousel"}</Tooltip>}
                      >
                        <span>
                          <Switch
                            checked={brand.promotion?.promoteOnLanding === true}
                            onChange={() => handleUpdateBrand(brand._id, {
                              ...brand,
                              promotion: { ...(brand.promotion || {}), promoteOnLanding: !brand.promotion?.promoteOnLanding },
                            })}
                            disabled={!canUpdateBrands || toggleLoading[brand._id]}
                            size="small"
                            classes={{ switchBase: classes.switchBase, track: classes.track, checked: classes.checked }}
                            color="primary"
                          />
                        </span>
                      </OverlayTrigger>
                      {/* Every toggle that sets toggleLoading now shows it.
                          Before, the row's only spinner was in the ACTIVE
                          column, so pressing this one appeared to spin a
                          different control. */}
                      {toggleLoading[brand._id] && (
                        <Spinner animation="border" size="sm" style={{ color: BRAND_MID, marginLeft: 4 }} />
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Switch
                          checked={brand.isActive !== false}
                          onChange={() => handleToggleActive(brand._id, brand.isActive)}
                          disabled={!canApproveBrands || toggleLoading[brand._id]}
                          size="small"
                          title={
                            brand.isActive !== false
                              ? "Deactivate — hides the brand and its whole catalogue from shoppers"
                              : brand.activatedAt
                              ? "Activate — the brand and its enabled products become public again"
                              : "Activate — this brand has never been public. Its enabled products go live immediately."
                          }
                          classes={{ switchBase: classes.switchBase, track: classes.track, checked: classes.checked }}
                        />
                        {toggleLoading[brand._id] && <Spinner animation="border" size="sm" style={{ color: BRAND_MID }} />}
                      </div>
                    </td>
                    <td className="col-actions">
                      <div className="bm-action-cell">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit brand</Tooltip>}>
                          <Button className="bm-action-btn" style={{ background: BRAND_MID, color: "white" }} size="sm" onClick={() => openEditModal(brand)} disabled={!canUpdateBrands}><FaEdit /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Add outlet</Tooltip>}>
                          <Button className="bm-action-btn" variant="success" size="sm" onClick={() => openOutletModal(brand)} disabled={!canCreateBrands}><FaCodeBranch /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Manage hours</Tooltip>}>
                          <Button className="bm-action-btn" variant="warning" size="sm" onClick={() => handleManageHours(brand)} disabled={!canUpdateBrands}><FaClock /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>{isSuperAdmin ? "Email the owner their sign-in details" : "Super admins only"}</Tooltip>}>
                          <Button
                            className="bm-action-btn"
                            variant="info"
                            size="sm"
                            onClick={() => handleResendOwnerSetupLink(brand)}
                            disabled={!isSuperAdmin || ownerInviteLoading[brand._id]}
                          >
                            {ownerInviteLoading[brand._id] ? <Spinner as="span" animation="border" size="sm" style={{ color: "white" }} /> : <FaEnvelope style={{ color: "white" }} />}
                          </Button>
                        </OverlayTrigger>
                        {/* For when the email above is not getting through: set
                            the owner's login email and a password, and send
                            them by hand. Super admins only (server-enforced). */}
                        <SetVendorLoginAction
                          vendorType="BRAND"
                          vendorId={brand._id}
                          vendorName={brand.name}
                          visible={isSuperAdmin}
                        >
                          {({ open }) => (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Set login email and password (send it yourself)</Tooltip>}>
                              <Button className="bm-action-btn" style={{ background: "#0f5132", color: "white" }} size="sm" onClick={open}>
                                <FaUserLock />
                              </Button>
                            </OverlayTrigger>
                          )}
                        </SetVendorLoginAction>
                        <SignInAsVendorButton
                          ownerId={brand.owner?._id || brand.owner}
                          vendorId={brand._id}
                          vendorName={brand.name}
                        >
                          {({ open, busy }) => (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Sign in as vendor (audited, 60 minutes)</Tooltip>}>
                              <Button className="bm-action-btn" style={{ background: "#4B0082", color: "white" }} size="sm" onClick={open} disabled={busy}>
                                {busy ? <Spinner as="span" animation="border" size="sm" style={{ color: "white" }} /> : <FaUserSecret />}
                              </Button>
                            </OverlayTrigger>
                          )}
                        </SignInAsVendorButton>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Manage products</Tooltip>}>
                          <Button className="bm-action-btn" variant="secondary" size="sm" onClick={() => history.push("/admin/brand-product-management", { brandId: brand._id, brandName: brand.name })} disabled={!canUpdateBrands}><FaShoppingBag /></Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete brand</Tooltip>}>
                          <Button className="bm-action-btn" variant="danger" size="sm" onClick={() => openDeleteModal(brand._id)} disabled={!canDeleteBrands}><FaTrash /></Button>
                        </OverlayTrigger>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          </div>
          {/* Below the table rather than in a row: a single cell spanning a
              1120px scrolling table lands off-centre on a narrow screen. */}
          {brands.length > 0 && visibleBrands.length === 0 && (
            <div className="bm-search-empty">
              <div>No brand matches "{brandQuery.trim()}"</div>
              <Button size="sm" variant="outline-secondary" style={{ borderRadius: 8 }} onClick={() => setBrandQuery("")}>
                Clear the search
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Create Brand Modal ── */}
      {/* Names the exact asset and says where it goes. The native confirm this
          replaced could only manage one grey sentence under "localhost:3000
          says", and froze the page while the delete ran. */}
      <ConfirmDialog
        show={!!mediaConfirm}
        tone="danger"
        title={`Delete ${mediaConfirm?.label?.toLowerCase() || "image"}?`}
        body={
          <>
            This permanently removes the{" "}
            <strong>{mediaConfirm?.label?.toLowerCase()}</strong> from{" "}
            <strong>{selectedBrand?.name || "this brand"}</strong> and from
            Cloudinary.
          </>
        }
        note="This cannot be undone. You can upload a replacement afterwards."
        confirmLabel={`Delete ${mediaConfirm?.label?.toLowerCase() || "image"}`}
        busyLabel="Deleting…"
        busy={!!mediaConfirm && !!removingMedia[mediaConfirm.type]}
        onConfirm={() => performRemoveBrandMedia(mediaConfirm)}
        onCancel={() => setMediaConfirm(null)}
      />

      <Modal show={showCreateModal} onHide={() => { discardSessionUploads(); setShowCreateModal(false); resetForm(); }} size="xl" dialogClassName="compact-modal">
        <Modal.Header closeButton className="bm-modal-header">
          <Modal.Title>
            <FaStore style={{ marginRight: 10, fontSize: "0.9rem" }} />
            Create New Brand
          </Modal.Title>
        </Modal.Header>
        <Form noValidate className={formSubmitAttempted ? "was-validated" : ""} onSubmit={handleCreateBrand}>
          <Modal.Body style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto", padding: "20px 24px", background: "#f8fafc" }}>
            {isSuperAdmin && (
              <div style={{ marginBottom: 14 }}>
                <Form.Group>
                  <Form.Label className="bm-label">Existing Owner (User ID, optional)</Form.Label>
                  <Form.Control className="bm-control" size="sm" name="owner" value={formData.owner} onChange={handleInputChange} placeholder="Leave blank to auto-create or link the owner from the brand email" />
                  <Form.Text className="bm-switch-help">
                    If this is left empty, Lusuku will create or link a brand-owner account from the brand email and send a password setup link when needed.
                  </Form.Text>
                </Form.Group>
              </div>
            )}
            <BrandFormBody {...formBodyProps} />
          </Modal.Body>
          <Modal.Footer style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
            {/* Says what happens next, at the moment the decision is made.
                Nobody should have to discover from an empty storefront that
                new brands are not public. */}
            <div className="bm-create-note">
              <FaInfoCircle style={{ marginRight: 6, flexShrink: 0 }} />
              <span>
                New brands are created <strong>deactivated</strong>. Build the
                catalogue first, then a super admin activates the brand to make
                it public.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
            <Button className="bm-btn-secondary" variant="outline-secondary" size="sm" onClick={() => { discardSessionUploads(); setShowCreateModal(false); resetForm(); }} disabled={isSubmitting}>Cancel</Button>
            <Button className="bm-btn-primary" size="sm" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Creating…</> : "Create Brand"}
            </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Edit Brand Modal ── */}
      <Modal show={showEditModal} onHide={() => { discardSessionUploads(); setShowEditModal(false); resetForm(); }} size="xl" dialogClassName="compact-modal">
        <Modal.Header closeButton className="bm-modal-header">
          <Modal.Title>
            <FaEdit style={{ marginRight: 10, fontSize: "0.9rem" }} />
            Edit Brand — {selectedBrand?.name}
          </Modal.Title>
        </Modal.Header>
        <Form noValidate className={formSubmitAttempted ? "was-validated" : ""} onSubmit={handleEditBrand}>
          <Modal.Body style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto", padding: "20px 24px", background: "#f8fafc" }}>
            {loadingBrandContent && (
              <div className="featured-notice" role="status" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "9px 12px", border: "1px solid #bfdbfe", borderRadius: 8, background: "#eff6ff", color: "#1e3a5f", fontSize: "0.75rem", fontWeight: 600 }}>
                <Spinner animation="border" size="sm" />
                Reading the latest saved brand profile and carousel content…
              </div>
            )}
            <fieldset disabled={loadingBrandContent} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
              <BrandFormBody {...formBodyProps} />
            </fieldset>
          </Modal.Body>
          <Modal.Footer style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0" }}>
            <Button className="bm-btn-secondary" variant="outline-secondary" size="sm" onClick={() => { discardSessionUploads(); setShowEditModal(false); resetForm(); }} disabled={isSubmitting}>Cancel</Button>
            <Button className="bm-btn-primary" size="sm" type="submit" disabled={isSubmitting || loadingBrandContent}>
              {isSubmitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Updating…</> : "Update Brand"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Add Outlet Modal (shared with OutletManagementBrand) ── */}
      <BrandOutletFormModal
        show={showOutletModal}
        onHide={() => setShowOutletModal(false)}
        brand={selectedBrand}
        canEditLocation={canCreateBrands}
        isSubmitting={isSubmitting}
        onSubmit={handleCreateOutlet}
      />

      {/* ── Delete confirmation ── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton className="bm-modal-header">
          <Modal.Title><FaTrash style={{ marginRight: 8 }} />Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FaTrash style={{ color: "#dc2626", fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: BRAND_DARK, marginBottom: 6 }}>Are you sure?</div>
              <div style={{ color: "#6b7280", fontSize: "0.9rem" }}>This will permanently delete the brand and cannot be undone. All associated data (outlets, products) may also be affected.</div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0" }}>
          <Button variant="outline-secondary" className="bm-btn-secondary" size="sm" onClick={() => setShowDeleteModal(false)} disabled={isSubmitting}>Cancel</Button>
          <Button variant="danger" size="sm" style={{ borderRadius: 8 }} onClick={handleDeleteBrand} disabled={isSubmitting}>
            {isSubmitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Deleting…</> : "Delete Brand"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Operating hours ── */}
      <Modal show={showHoursModal} onHide={() => setShowHoursModal(false)} size="lg">
        <Modal.Header closeButton className="bm-modal-header">
          <Modal.Title><FaClock style={{ marginRight: 10 }} />Operating Hours — {selectedBrand?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          {/* Brands trade 24/7 until hours are saved. */}
          <OpenHoursManager
            vendorLabel="brand"
            tradingPolicy="open_until_set"
            vendorType="brand"
            vendorId={selectedBrand?._id}
            hours={brandHours}
            onSave={handleSaveHours}
            isSaving={isSavingHours}
            timezone={selectedBrand?.timezone || "Africa/Kampala"}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BrandManagement;
